import { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle, type ForwardedRef } from 'react';
import {
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  Undo,
  Redo,
  Palette,
  Highlighter,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Eraser,
  Image as ImageIcon,
  Smile,
  CheckSquare,
  Lightbulb,
  Target,
  Strikethrough,
  Subscript,
  Superscript,
  Link as LinkIcon,
  Code,
  Quote,
  Minus,
  Table as TableIcon,
  IndentIncrease,
  IndentDecrease,
  X,
  Music,
  Video,
  Youtube,
  Upload,
  Link2,
  ChevronDown,
} from 'lucide-react';
import TooltipArrow from '@/components/ui/TooltipArrow';
import { storageService } from '@/services/storage.service';
import { toast } from 'react-hot-toast';
import ImageResizeHandler from './ImageResizeHandler';
import MediaControlHandler from './MediaControlHandler';
import DragDropHandler from './DragDropHandler';
import ComponentConfigModal, { ComponentType } from './ComponentConfigModal';
import MediaInsertModal, { MediaInsertType } from './MediaInsertModal';
import './ComponentEditButtons.css';

export interface VisualContentEditorProps {
  initialContent: string;
  onChange: (html: string) => void;
  onSave?: () => void;
  readOnly?: boolean;
  className?: string;
  hideVideoInsert?: boolean;
  hideAudioInsert?: boolean;
  hideKnowledgeCheck?: boolean;
  /** Drop the Insert ribbon tab (media, tables, links, components, callouts). */
  hideInsertTab?: boolean;
  /**
   * Drop the Format ribbon tab. Loses nothing on its own — every control it holds
   * (text colour, highlight, subscript, superscript) is also in Home.
   */
  hideFormatTab?: boolean;
  isRTL?: boolean;
}

/**
 * Imperative handle so a parent can read the editor's CURRENT content
 * synchronously — bypassing the debounced onChange. Use before saving so a
 * quick type-then-save never persists stale (pre-edit) HTML.
 */
export interface VisualContentEditorHandle {
  /** Reconstruct and return the full HTML document from the live editor DOM. */
  getContent: () => string;
  /** Flush any pending debounced edit through onChange immediately. */
  flush: () => void;
}

/**
 * Visual content editor using contenteditable
 * Preserves ALL HTML structure, classes, and styles from generated content
 * Shows content exactly as it appears in preview
 */
function VisualContentEditor({
  initialContent = '',
  onChange,
  onSave,
  readOnly = false,
  className = '',
  hideVideoInsert = false,
  hideAudioInsert = false,
  hideKnowledgeCheck = false,
  hideInsertTab = false,
  hideFormatTab = false,
  isRTL = false,
}: VisualContentEditorProps, ref: ForwardedRef<VisualContentEditorHandle>) {
  const editorRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const audioInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const [extractedStyles, setExtractedStyles] = useState<string>('');
  const [isInitialized, setIsInitialized] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showBgColorPicker, setShowBgColorPicker] = useState(false);
  const savedSelectionRef = useRef<Range | null>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [showImageDropdown, setShowImageDropdown] = useState(false);
  const [showAudioDropdown, setShowAudioDropdown] = useState(false);
  const [showVideoDropdown, setShowVideoDropdown] = useState(false);
  const [mediaInsertModalOpen, setMediaInsertModalOpen] = useState(false);
  const [mediaInsertType, setMediaInsertType] = useState<MediaInsertType>('image-url');
  const [componentModalOpen, setComponentModalOpen] = useState(false);
  const [componentModalType, setComponentModalType] = useState<ComponentType>('learning-objectives');
  const [editingComponentElement, setEditingComponentElement] = useState<HTMLElement | null>(null);
  const [showTableModal, setShowTableModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);
  const [activeRibbonTab, setActiveRibbonTab] = useState<'home' | 'insert' | 'format'>('home');
  const [showFindReplace, setShowFindReplace] = useState(false);
  const [findText, setFindText] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [findMatches, setFindMatches] = useState<number>(0);
  const [showTableContextMenu, setShowTableContextMenu] = useState(false);
  const [tableContextMenuPosition, setTableContextMenuPosition] = useState({ x: 0, y: 0 });
  const [selectedTableElement, setSelectedTableElement] = useState<HTMLTableElement | null>(null);
  const [selectedTableCell, setSelectedTableCell] = useState<HTMLTableCellElement | null>(null);
  const [fullHTMLStructure, setFullHTMLStructure] = useState<{
    doctype: string;
    htmlTag: string;
    headContent: string;
    bodyTag: string;
    scripts: string;
  } | null>(null);


  // Custom undo/redo history stack for JavaScript DOM operations
  const historyStackRef = useRef<string[]>([]);
  const historyIndexRef = useRef<number>(-1);
  const isUndoRedoRef = useRef<boolean>(false);
  const maxHistorySize = 50;

  // Track active formatting state for toolbar buttons
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
    strikeThrough: false,
    subscript: false,
    superscript: false,
    justifyLeft: false,
    justifyCenter: false,
    justifyRight: false,
    justifyFull: false,
    insertUnorderedList: false,
    insertOrderedList: false,
    fontName: '',
    formatBlock: '',
    foreColor: '',
    hiliteColor: '',
  });

  // Helper to convert RGB to Hex
  const rgbToHex = (rgb: string): string => {
    if (!rgb || rgb === 'transparent') return '';
    if (rgb.startsWith('#')) return rgb.toLowerCase();
    const match = rgb.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
    if (match) {
      const hex = (x: string) => parseInt(x).toString(16).padStart(2, '0');
      return `#${hex(match[1])}${hex(match[2])}${hex(match[3])}`.toLowerCase();
    }
    return rgb.toLowerCase();
  };

  // Helper to escape HTML special characters for code blocks
  const escapeHtml = (text: string): string => {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  // Check current formatting state at cursor/selection
  const checkFormattingState = useCallback(() => {
    if (readOnly) return;

    // Make sure we're checking selection within the editor
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    if (!editorRef.current?.contains(selection.anchorNode)) return;

    // Get the current block format (p, h1, h2, etc.)
    let formatBlock = '';
    try {
      const block = document.queryCommandValue('formatBlock');
      // Normalize the format block value - remove tags and lowercase
      if (block) {
        formatBlock = block.toLowerCase().replace(/<|>/g, '').trim();
        // Map common variations
        if (formatBlock === 'div') formatBlock = 'p';
        if (formatBlock === 'normal') formatBlock = 'p';
      }
    } catch {
      formatBlock = '';
    }

    // Get font name - normalize to match dropdown options
    let fontName = '';
    try {
      const rawFontName = document.queryCommandValue('fontName') || '';
      // Clean up font name (remove quotes and extra spaces)
      fontName = rawFontName.replace(/['"]/g, '').trim();

      // Map common font name variations to match dropdown options
      const fontNameMap: Record<string, string> = {
        'arial': 'Arial',
        'times new roman': 'Times New Roman',
        'times': 'Times New Roman',
        'georgia': 'Georgia',
        'verdana': 'Verdana',
        'courier new': 'Courier New',
        'courier': 'Courier New',
        'comic sans ms': 'Comic Sans MS',
        'impact': 'Impact',
        'trebuchet ms': 'Trebuchet MS',
        'tahoma': 'Tahoma',
        'palatino linotype': 'Palatino Linotype',
        'lucida console': 'Lucida Console',
      };

      const lowerFontName = fontName.toLowerCase();

      // Check for system fonts and map to "System Default"
      if (lowerFontName.includes('-apple-system') ||
          lowerFontName.includes('blinkmacfont') ||
          lowerFontName.includes('segoe ui') ||
          lowerFontName.includes('system-ui') ||
          lowerFontName === 'roboto' ||
          lowerFontName === 'sans-serif' ||
          lowerFontName.includes('cantarell') ||
          lowerFontName.includes('ubuntu')) {
        fontName = 'System Default';
      } else if (fontNameMap[lowerFontName]) {
        fontName = fontNameMap[lowerFontName];
      }
    } catch {
      fontName = '';
    }

    // Get text color
    let foreColor = '';
    try {
      foreColor = rgbToHex(document.queryCommandValue('foreColor') || '');
    } catch {
      foreColor = '';
    }

    // Get highlight color
    let hiliteColor = '';
    try {
      hiliteColor = rgbToHex(document.queryCommandValue('hiliteColor') || document.queryCommandValue('backColor') || '');
    } catch {
      hiliteColor = '';
    }

    const newFormats = {
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      strikeThrough: document.queryCommandState('strikeThrough'),
      subscript: document.queryCommandState('subscript'),
      superscript: document.queryCommandState('superscript'),
      justifyLeft: document.queryCommandState('justifyLeft'),
      justifyCenter: document.queryCommandState('justifyCenter'),
      justifyRight: document.queryCommandState('justifyRight'),
      justifyFull: document.queryCommandState('justifyFull'),
      insertUnorderedList: document.queryCommandState('insertUnorderedList'),
      insertOrderedList: document.queryCommandState('insertOrderedList'),
      fontName,
      formatBlock,
      foreColor,
      hiliteColor,
    };

    setActiveFormats(newFormats);
  }, [readOnly]);

  // Fix existing buttons in content to prevent form submission
  const fixExistingButtons = (html: string): string => {
    // Add type="button" to all buttons that don't have it
    return html.replace(
      /<button(\s+[^>]*?)(?<!type=["'][^"']*["'])>/gi,
      (match, attrs) => {
        // Check if type attribute already exists
        if (/type\s*=/.test(match)) {
          return match; // Already has type, don't modify
        }
        // Add type="button" before the closing >
        return `<button type="button"${attrs}>`;
      }
    );
  };

  // Helper: scope individual CSS rules to .editor-content-preview
  const scopeRules = (css: string): string => {
    const rules: string[] = [];
    const ruleRegex = /([^{}]+)\{([^{}]*)\}/g;
    let match;
    while ((match = ruleRegex.exec(css)) !== null) {
      const rawSelector = match[1].trim();
      const properties = match[2];
      if (!rawSelector || rawSelector.startsWith('@') || !properties.trim()) continue;

      const selectorParts = rawSelector.split(',').map(s => s.trim());
      const scopedSelectors = selectorParts.map(t => {
        if (!t) return '';
        if (/^(body|html|\*|:root)$/i.test(t)) return '.editor-content-preview';
        return `.editor-content-preview ${t}`;
      }).filter(Boolean).join(', ');
      rules.push(`${scopedSelectors} { ${properties} }`);
    }
    return rules.join('\n');
  };

  // Scope ALL styles to .editor-content-preview to prevent them from affecting the modal/sidebar
  // Preserves all visual styles (backgrounds, colors, animations, fonts) — only scopes selectors
  const scopeStylesForEditor = (css: string): string => {
    // Strip CSS comments
    let cleaned = css.replace(/\/\*[\s\S]*?\*\//g, '');

    const output: string[] = [];

    // Preserve @keyframes as-is (animation definitions, don't target elements)
    const keyframeBlocks: string[] = [];
    cleaned = cleaned.replace(/@keyframes\s+[^{]+\{[^}]*(?:\{[^}]*\}[^}]*)*\}/gi, (m) => {
      keyframeBlocks.push(m);
      return '';
    });

    // Preserve @font-face as-is
    const fontFaceBlocks: string[] = [];
    cleaned = cleaned.replace(/@font-face\s*\{[^}]*\}/gi, (m) => {
      fontFaceBlocks.push(m);
      return '';
    });

    // Remove @import (can't be scoped, may cause issues inside <style> injection)
    cleaned = cleaned.replace(/@import\s+[^;]+;/gi, '');

    // Handle @media blocks — scope the rules inside them
    cleaned = cleaned.replace(/@media\s+([^{]+)\{([\s\S]*?\})\s*\}/gi, (_match, query, body) => {
      const scopedBody = scopeRules(body);
      if (scopedBody.trim()) {
        output.push(`@media ${query.trim()} { ${scopedBody} }`);
      }
      return '';
    });

    // Scope remaining top-level rules
    output.push(scopeRules(cleaned));

    // Add back @keyframes and @font-face
    return [...keyframeBlocks, ...fontFaceBlocks, ...output].join('\n');
  };

  // Strip leading icons from learning-objectives/key-takeaways list items when in bullet mode
  const stripLeadingIconsFromBulletLists = (html: string): string => {
    // Icon characters to match (comprehensive list)
    const iconChars = '✓✔☑✅⬜☐•◦‣⁃→►▸▹★☆●○◆◇🎯📚🎓📝✔️💡🔑⚡✨🌟💫🎁📍';
    const iconPattern = /^[\s]*[✓✔☑✅⬜☐•◦‣⁃→►▸▹★☆●○◆◇🎯📚🎓📝✔️💡🔑⚡✨🌟💫🎁📍\u2713\u2714\u2611\u2705]+[\s]*/;

    // Parse HTML to find learning-objectives and key-takeaways
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');

    // Find ALL learning-objectives and key-takeaways - strip icons from all of them
    // This is more aggressive but ensures no duplicate bullets/icons
    const allComponents = doc.querySelectorAll('.learning-objectives, .key-takeaways');

    allComponents.forEach((component) => {
      const listItems = component.querySelectorAll('li');
      listItems.forEach((li) => {
        // Method 1: Check if first child is a span containing only icons
        const firstChild = li.firstChild;
        if (firstChild && firstChild.nodeType === Node.ELEMENT_NODE) {
          const el = firstChild as HTMLElement;
          const text = el.textContent?.trim() || '';
          // If it's a span/element with only icon characters (1-3 chars), remove it
          if (text.length <= 3 && [...text].every(c => iconChars.includes(c))) {
            el.remove();
          }
        }

        // Method 2: Handle text node with leading icons
        const currentFirstChild = li.firstChild;
        if (currentFirstChild && currentFirstChild.nodeType === Node.TEXT_NODE) {
          const text = currentFirstChild.textContent || '';
          const cleanText = text.replace(iconPattern, '');
          if (cleanText !== text) {
            currentFirstChild.textContent = cleanText;
          }
        }

        // Method 3: Remove icon-only spans from innerHTML
        let innerHTML = li.innerHTML;
        // Remove span elements that contain only icons
        innerHTML = innerHTML.replace(/<span[^>]*>[\s]*[✓✔☑✅⬜☐•◦‣⁃→►▸▹★☆●○◆◇🎯📚🎓📝✔️💡🔑⚡✨🌟💫🎁📍\u2713\u2714\u2611\u2705]+[\s]*<\/span>[\s]*/gi, '');
        // Remove leading icon text
        innerHTML = innerHTML.replace(iconPattern, '');

        if (innerHTML !== li.innerHTML) {
          li.innerHTML = innerHTML;
        }
      });

      // Set default data attribute if missing
      if (!component.hasAttribute('data-use-custom-icon')) {
        component.setAttribute('data-use-custom-icon', 'false');
      }
    });

    // Return the modified HTML (extract from the wrapper div)
    const wrapper = doc.body.firstChild as HTMLElement;
    return wrapper ? wrapper.innerHTML : html;
  };

  // Define global toggleAnswer function for Knowledge Check components
  // This ensures both AI-generated and newly created Knowledge Checks work in edit mode
  useEffect(() => {
    // Define the toggleAnswer function globally so onclick handlers can use it
    (window as any).toggleAnswer = (answerId: string, buttonElement: HTMLButtonElement) => {
      const answerElement = document.getElementById(answerId);
      if (!answerElement) return;

      if (answerElement.style.display === 'none' || answerElement.style.display === '') {
        answerElement.style.display = 'block';
        buttonElement.textContent = 'Hide Answer';
        buttonElement.style.backgroundColor = '#dc3545';
      } else {
        answerElement.style.display = 'none';
        buttonElement.textContent = 'Show Answer';
        buttonElement.style.backgroundColor = '#3b82f6';
      }
    };

    // Cleanup on unmount
    return () => {
      delete (window as any).toggleAnswer;
    };
  }, []);

  // Extract CSS from HTML content and initialize editor
  // Re-run when initialContent changes (e.g., theme changes)
  useEffect(() => {
    if (initialContent) {
      // Parse and store complete HTML structure
      const doctypeMatch = initialContent.match(/<!DOCTYPE[^>]*>/i);
      const htmlTagMatch = initialContent.match(/<html[^>]*>/i);
      const headMatch = initialContent.match(/<head[^>]*>([\s\S]*?)<\/head>/i);
      const bodyTagMatch = initialContent.match(/<body[^>]*>/i);

      setFullHTMLStructure({
        doctype: doctypeMatch ? doctypeMatch[0] : '<!DOCTYPE html>',
        htmlTag: htmlTagMatch ? htmlTagMatch[0] : '<html>',
        headContent: headMatch ? headMatch[1] : '',
        bodyTag: bodyTagMatch ? bodyTagMatch[0] : '<body>',
        scripts: '' // Scripts stay in body content
      });

      // Extract styles for display (re-extract on every content change to support theme changes)
      const styleMatches = initialContent.match(/<style[^>]*>([\s\S]*?)<\/style>/gi) || [];
      const styles = styleMatches
        .map((styleTag) => styleTag.replace(/<\/?style[^>]*>/gi, ''))
        .join('\n');

      // Scope the styles to prevent body styles from affecting entire page
      const scopedStyles = scopeStylesForEditor(styles);
      setExtractedStyles(scopedStyles);

      // Extract body content for editing (KEEP scripts inside body)
      let bodyContent = initialContent;
      const bodyMatch = initialContent.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
      if (bodyMatch) {
        bodyContent = bodyMatch[1]; // Includes scripts
      }

      // Fix any existing buttons to prevent form submission
      bodyContent = fixExistingButtons(bodyContent);

      // Strip leading icons from learning-objectives in bullet mode
      bodyContent = stripLeadingIconsFromBulletLists(bodyContent);

      // Set content directly - don't sanitize (generated content is trusted)
      // Only update editor content if not initialized yet to avoid losing user edits
      if (editorRef.current && !isInitialized) {
        editorRef.current.innerHTML = bodyContent;
        setIsInitialized(true);
        // Initialize history stack with initial content
        historyStackRef.current = [bodyContent];
        historyIndexRef.current = 0;
        // Arm wipe protection from the first keystroke, not only after one edit.
        lastNonEmptyHtmlRef.current = bodyContent;
      }
    }
  }, [initialContent, isInitialized]);

  // Sanitize learning-objectives in the editor DOM directly after initialization
  useEffect(() => {
    if (isInitialized && editorRef.current) {
      const iconChars = '✓✔☑✅⬜☐•◦‣⁃→►▸▹★☆●○◆◇🎯📚🎓📝✔️💡🔑⚡✨🌟💫🎁📍';
      const iconPattern = /^[\s]*[✓✔☑✅⬜☐•◦‣⁃→►▸▹★☆●○◆◇🎯📚🎓📝✔️💡🔑⚡✨🌟💫🎁📍\u2713\u2714\u2611\u2705]+[\s]*/;

      // Find ALL learning-objectives and key-takeaways in the editor
      const allComponents = editorRef.current.querySelectorAll('.learning-objectives, .key-takeaways');

      let contentChanged = false;
      allComponents.forEach((component) => {
        const listItems = component.querySelectorAll('li');
        listItems.forEach((li) => {
          // Method 1: Check if first child is an element with only icon characters
          const firstChild = li.firstChild;
          if (firstChild && firstChild.nodeType === Node.ELEMENT_NODE) {
            const el = firstChild as HTMLElement;
            const text = el.textContent?.trim() || '';
            if (text.length <= 3 && [...text].every(c => iconChars.includes(c))) {
              el.remove();
              contentChanged = true;
            }
          }

          // Method 2: Remove icon-only spans and leading icons from innerHTML
          let innerHTML = li.innerHTML;
          // Remove span elements with only icons
          innerHTML = innerHTML.replace(/<span[^>]*>[\s]*[✓✔☑✅⬜☐•◦‣⁃→►▸▹★☆●○◆◇🎯📚🎓📝✔️💡🔑⚡✨🌟💫🎁📍\u2713\u2714\u2611\u2705]+[\s]*<\/span>[\s]*/gi, '');
          // Remove leading icons
          innerHTML = innerHTML.replace(iconPattern, '');

          if (innerHTML !== li.innerHTML) {
            li.innerHTML = innerHTML;
            contentChanged = true;
          }
        });

        // Set default data attribute if missing
        if (!component.hasAttribute('data-use-custom-icon')) {
          component.setAttribute('data-use-custom-icon', 'false');
        }
      });

      // Trigger onChange if content was modified
      if (contentChanged) {
        const newContent = editorRef.current.innerHTML;
        onChange(newContent);
      }
    }
  }, [isInitialized]);

  // Fix emoji encoding issues - convert HTML entities back to actual emojis
  const fixEmojiEncoding = (html: string): string => {
    // Convert numeric HTML entities (&#XXXXX;) to actual emojis
    // This prevents encoding issues and keeps emojis as emojis
    return html.replace(/&#(\d+);/g, (match, dec) => {
      const codePoint = parseInt(dec, 10);
      // Only convert emoji range (most emojis are in these ranges)
      if (
        (codePoint >= 0x1f300 && codePoint <= 0x1f9ff) || // Emoticons, symbols
        (codePoint >= 0x2600 && codePoint <= 0x26ff) ||   // Misc symbols
        (codePoint >= 0x2700 && codePoint <= 0x27bf) ||   // Dingbats
        (codePoint >= 0x1f000 && codePoint <= 0x1f2ff) || // Mahjong, cards
        (codePoint >= 0x1fa00 && codePoint <= 0x1faff)    // Extended symbols
      ) {
        return String.fromCodePoint(codePoint);
      }
      return match; // Keep other entities as-is
    });
  };

  /**
   * Controls the editor injects into the live DOM for authoring. They are not part
   * of the lesson and must be ignored when deciding whether the slide is gone:
   * DragDropHandler gives every component a delete button whose text is '×' and a
   * drag handle built from an inline <svg> (DragDropHandler.tsx:57-99). Left in,
   * that '×' reads as text, that <svg> reads as media, and both read as structure,
   * so a fully-wiped slide would look intact and the guard would not fire.
   */
  const EDITOR_CHROME_SELECTOR =
    '.component-delete-btn, .component-delete-btn-dynamic, .component-drag-handle, ' +
    '.drop-placeholder, .media-delete-btn, .image-action-buttons, .resize-handle';

  /** The last state the editor held that was NOT empty — used to undo a wipe. */
  const lastNonEmptyHtmlRef = useRef<string | null>(null);

  /**
   * True when the editor holds no SLIDE left at all — the whole document has been
   * removed, not merely emptied of words.
   *
   * Clearing the text is a legitimate edit: an author blanking a slide to retype it
   * keeps the slide's own markup (headings, panels, list containers), so structure
   * survives and this returns false. Only when nothing remains — no text, no media,
   * and no element other than a stray <br> the browser leaves behind — is it treated
   * as a wipe worth blocking.
   *
   * CSS, JS and the editor's own chrome are discarded first so they cannot make an
   * emptied slide look like it still has content (DragDropHandler's '×' button reads
   * as text and its drag handle is an inline <svg>).
   */
  const isEditorEmpty = (root: HTMLElement): boolean => {
    const clone = root.cloneNode(true) as HTMLElement;
    clone
      .querySelectorAll(`script, style, noscript, template, ${EDITOR_CHROME_SELECTOR}`)
      .forEach((el) => el.remove());
    const hasText = (clone.textContent || '').replace(/\s+/g, ' ').trim().length > 0;
    // Queried on the CLONE, so the drag handle's <svg> cannot masquerade as media.
    const hasMedia = !!clone.querySelector('img, audio, video, iframe, table, svg');
    // Any surviving element IS the slide. <br> does not count: browsers insert one
    // into an emptied contenteditable, so it would make a total wipe look structured.
    const hasStructure = Array.from(clone.querySelectorAll('*')).some(
      (el) => el.tagName !== 'BR'
    );
    return !hasText && !hasMedia && !hasStructure;
  };

  const warnContentProtected = () => {
    toast.error('The slide itself cannot be deleted. Clear the text instead, or replace it.', {
      id: 'slide-content-protected', // one toast, not one per keystroke
    });
  };

  /**
   * ===== WIPE PROTECTION (pre-emptive) =====
   * Blocks a deletion that would leave the slide with nothing on it, BEFORE the
   * DOM is touched, so the content is never cleared — the user just gets the toast.
   *
   * Runs on the NATIVE `beforeinput` event, which fires while the doomed content
   * is still in place. React's `onBeforeInput` is a synthetic polyfill built from
   * textInput/composition events — it never fires for deletions and carries no
   * target ranges — so this is registered with addEventListener below.
   *
   * The would-be result is simulated on a clone: the event's own target range is
   * mapped onto that clone and deleted there, so the live slide is untouched no
   * matter what the answer turns out to be.
   *
   * Deletions only. Replacing everything by typing or pasting over a full
   * selection is legitimate and left alone.
   */
  const handleBeforeInput = useCallback((native: InputEvent) => {
    const editor = editorRef.current;
    if (!editor || readOnly) return;

    if (!native.inputType?.startsWith('delete')) return;

    // getTargetRanges() reports exactly what the browser is about to remove —
    // including the whole-block removal a collapsed cursor triggers, which the
    // selection alone does not reveal.
    // Not every browser fills getTargetRanges() in (Firefox leaves it empty for
    // some delete types); the live selection is the fallback, and when neither is
    // usable the post-check in handleInput still covers it.
    const targetRange = native.getTargetRanges?.()[0];
    let range: Range | null = null;
    if (targetRange) {
      range = document.createRange();
      range.setStart(targetRange.startContainer, targetRange.startOffset);
      range.setEnd(targetRange.endContainer, targetRange.endOffset);
    } else {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) range = selection.getRangeAt(0);
    }
    if (!range) return;

    // Clone the editor, map the doomed range onto that clone by child-index path,
    // and delete it THERE — so the live slide is never touched, whatever the answer.
    const probe = editor.cloneNode(true) as HTMLElement;
    const path: number[] = [];
    let node: Node | null = range.startContainer;
    // Only ranges inside the editor can be simulated against its clone.
    if (!editor.contains(node)) return;
    while (node && node !== editor) {
      const parent: Node | null = node.parentNode;
      if (!parent) return;
      path.unshift(Array.prototype.indexOf.call(parent.childNodes, node));
      node = parent;
    }
    const endPath: number[] = [];
    let endNode: Node | null = range.endContainer;
    if (!editor.contains(endNode)) return;
    while (endNode && endNode !== editor) {
      const parent: Node | null = endNode.parentNode;
      if (!parent) return;
      endPath.unshift(Array.prototype.indexOf.call(parent.childNodes, endNode));
      endNode = parent;
    }
    const resolve = (p: number[]): Node | null =>
      p.reduce<Node | null>((acc, i) => acc?.childNodes[i] ?? null, probe);
    const probeStart = resolve(path);
    const probeEnd = resolve(endPath);
    if (!probeStart || !probeEnd) return;

    const probeRange = document.createRange();
    try {
      probeRange.setStart(probeStart, range.startOffset);
      probeRange.setEnd(probeEnd, range.endOffset);
    } catch {
      return; // offsets did not survive the clone — fall through to the post-check
    }
    probeRange.deleteContents();

    if (isEditorEmpty(probe)) {
      native.preventDefault(); // the slide keeps its content; nothing is ever cleared
      warnContentProtected();
    }
  }, [readOnly]);

  // Native listener: see handleBeforeInput on why React's onBeforeInput cannot
  // be used here.
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || readOnly) return;
    const listener = (ev: Event) => handleBeforeInput(ev as InputEvent);
    editor.addEventListener('beforeinput', listener);
    return () => editor.removeEventListener('beforeinput', listener);
  }, [readOnly, handleBeforeInput]);

  /**
   * Reconstruct the complete HTML document from the live editor content.
   * Returns null when there is nothing safe to emit (no structure, or a wipe
   * was just reverted).
   */
  const buildFullHtml = useCallback((): string | null => {
    // Safety net behind handleBeforeInput, for any route that reaches the DOM
    // anyway (drag-out, a browser that skips beforeinput). Reverting is second
    // choice — the pre-emptive guard above is what keeps the slide from blinking
    // empty — so this only restores and refuses to emit the wiped document.
    if (editorRef.current && !readOnly) {
      if (isEditorEmpty(editorRef.current)) {
        const restore = lastNonEmptyHtmlRef.current;
        if (restore) {
          editorRef.current.innerHTML = restore;
          warnContentProtected();
          return null; // never emit the emptied document
        }
      } else {
        lastNonEmptyHtmlRef.current = editorRef.current.innerHTML;
      }
    }

    if (!editorRef.current || !fullHTMLStructure) return null;

    let editedBodyContent = editorRef.current.innerHTML;

    // Remove editor UI elements that shouldn't be saved
    // This includes drag handles, resize handles, action buttons, etc.
    editedBodyContent = editedBodyContent
      .replace(/<div[^>]*class="[^"]*component-drag-handle[^"]*"[^>]*>[\s\S]*?<\/div>/gi, '')
      .replace(/<div[^>]*class="[^"]*image-action-buttons[^"]*"[^>]*>[\s\S]*?<\/div>/gi, '')
      .replace(/<div[^>]*class="[^"]*resize-handle[^"]*"[^>]*>[\s\S]*?<\/div>/gi, '')
      .replace(/<div[^>]*class="[^"]*drop-placeholder[^"]*"[^>]*>[\s\S]*?<\/div>/gi, '')
      .replace(/<button[^>]*class="[^"]*media-delete-btn[^"]*"[^>]*>[\s\S]*?<\/button>/gi, '')
      .replace(/<button[^>]*class="[^"]*component-delete-btn[^"]*"[^>]*>[\s\S]*?<\/button>/gi, '')
      .replace(/<button[^>]*class="[^"]*component-delete-btn-dynamic[^"]*"[^>]*>[\s\S]*?<\/button>/gi, '')
      // Unwrap void elements from their wrapper (keep the content, remove wrapper div)
      .replace(/<div[^>]*class="[^"]*void-element-controls-wrapper[^"]*"[^>]*>([\s\S]*?)<\/div>/gi, '$1');

    // Clean up browser contenteditable artifacts inside headings
    // Browsers wrap edited text in <span style="color:..."> or <font> tags
    // which breaks gradient text and themed heading colors
    editedBodyContent = editedBodyContent.replace(
      /(<h[1-6][^>]*>)([\s\S]*?)(<\/h[1-6]>)/gi,
      (_match, openTag, content, closeTag) => {
        // Only strip spans that have NO class and ONLY color/font-size styles (browser artifacts)
        // Preserve spans with background, gradient, or class attributes (intentional styling)
        const cleaned = content
          .replace(/<span(?![^>]*class=)(?![^>]*background)(?![^>]*gradient)[^>]*>([\s\S]*?)<\/span>/gi, '$1')
          .replace(/<font[^>]*>([\s\S]*?)<\/font>/gi, '$1');
        return `${openTag}${cleaned}${closeTag}`;
      }
    );

    // Normalize &nbsp; to regular spaces (contenteditable artifact)
    // Except inside <pre> and <code> blocks where whitespace matters
    editedBodyContent = editedBodyContent.replace(
      /(<pre[\s\S]*?<\/pre>)|(<code[\s\S]*?<\/code>)|(&nbsp;)/gi,
      (match, pre, code, nbsp) => {
        if (pre || code) return match; // Preserve in code blocks
        if (nbsp) return ' ';
        return match;
      }
    );

    // Fix any emoji encoding issues
    editedBodyContent = fixEmojiEncoding(editedBodyContent);

    // Reconstruct COMPLETE HTML document
    const fullHTML = `${fullHTMLStructure.doctype}
${fullHTMLStructure.htmlTag}
<head>${fullHTMLStructure.headContent}</head>
${fullHTMLStructure.bodyTag}
${editedBodyContent}
${fullHTMLStructure.scripts}
</body>
</html>`;

    return fullHTML;
  }, [fullHTMLStructure, readOnly]);

  // Emit the current content through onChange (used by the debounce + flush).
  const emitContentNow = useCallback(() => {
    const html = buildFullHtml();
    if (html !== null) onChange(html);
  }, [buildFullHtml, onChange]);

  // Handle content changes — debounced to reduce re-renders (300ms).
  const handleInput = useCallback(() => {
    // The wipe check runs here too, synchronously in the same tick as the DOM
    // mutation rather than inside the debounce, so anything the pre-emptive
    // beforeinput guard missed is put back before the browser paints — the slide
    // is never seen empty. Only the onChange emit is debounced.
    if (editorRef.current && !readOnly && isEditorEmpty(editorRef.current)) {
      const restore = lastNonEmptyHtmlRef.current;
      if (restore) {
        editorRef.current.innerHTML = restore;
        warnContentProtected();
        return;
      }
    }

    if (editorRef.current && fullHTMLStructure) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        emitContentNow();
      }, 300);
    }
  }, [fullHTMLStructure, emitContentNow, readOnly]);

  // Flush any pending debounced edit synchronously. Called on blur so that
  // clicking a Save/toolbar button (which blurs the editor first) captures the
  // latest edit BEFORE the click handler reads the form value — otherwise a
  // quick type-then-save loses the last edit (notably on freshly-added scenes).
  const flushPendingContent = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
      emitContentNow();
    }
  }, [emitContentNow]);

  // Expose a synchronous read of the live editor content to parents. Saving
  // reads this directly so it never depends on the debounce having fired.
  useImperativeHandle(ref, () => ({
    getContent: () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      return buildFullHtml() ?? (editorRef.current?.innerHTML ?? '');
    },
    flush: () => flushPendingContent(),
  }), [buildFullHtml, flushPendingContent]);

  // Save current selection
  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      // Only save if selection is within the editor
      if (editorRef.current && editorRef.current.contains(range.commonAncestorContainer)) {
        savedSelectionRef.current = range.cloneRange();
      }
    }
  };

  // Save current state to custom history stack
  const saveToHistory = useCallback(() => {
    if (!editorRef.current || isUndoRedoRef.current) return;

    const currentContent = editorRef.current.innerHTML;

    // Don't save if content is the same as the last entry
    const lastContent = historyStackRef.current[historyIndexRef.current];
    if (lastContent === currentContent) return;

    // If we're not at the end of the stack, truncate future states
    if (historyIndexRef.current < historyStackRef.current.length - 1) {
      historyStackRef.current = historyStackRef.current.slice(0, historyIndexRef.current + 1);
    }

    // Add new state
    historyStackRef.current.push(currentContent);
    historyIndexRef.current = historyStackRef.current.length - 1;

    // Limit history size
    if (historyStackRef.current.length > maxHistorySize) {
      historyStackRef.current.shift();
      historyIndexRef.current--;
    }
  }, []);

  // Custom undo handler
  const handleUndo = useCallback(() => {
    if (!editorRef.current) return;

    // Save current state before undo if not already saved
    saveToHistory();

    if (historyIndexRef.current > 0) {
      isUndoRedoRef.current = true;
      historyIndexRef.current--;
      editorRef.current.innerHTML = historyStackRef.current[historyIndexRef.current];
      handleInput();
      isUndoRedoRef.current = false;
    } else {
      // Fall back to browser undo for text operations
      document.execCommand('undo');
      handleInput();
    }
  }, [handleInput, saveToHistory]);

  // Custom redo handler
  const handleRedo = useCallback(() => {
    if (!editorRef.current) return;

    if (historyIndexRef.current < historyStackRef.current.length - 1) {
      isUndoRedoRef.current = true;
      historyIndexRef.current++;
      editorRef.current.innerHTML = historyStackRef.current[historyIndexRef.current];
      handleInput();
      isUndoRedoRef.current = false;
    } else {
      // Fall back to browser redo for text operations
      document.execCommand('redo');
      handleInput();
    }
  }, [handleInput]);

  // Tag the color markup that execCommand just produced (a class-less
  // <span style="color…"> or legacy <font color>) with a marker class, and
  // normalise <font> → <span>. The save-time heading-cleanup strips ONLY
  // class-less spans/fonts inside headings (to protect themed gradient
  // headings), so without this a color applied to a heading is discarded on
  // save. A class makes the cleanup preserve it. Scoped to the current
  // selection so only the just-colored region is affected.
  const markUserColoredSpansInSelection = () => {
    if (!editorRef.current) return;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);

    const candidates = Array.from(editorRef.current.querySelectorAll('span, font'));
    candidates.forEach((el) => {
      try {
        if (!range.intersectsNode(el)) return;
      } catch {
        return;
      }
      const tag = el.tagName.toLowerCase();
      if (tag === 'font') {
        // Legacy <font color="…"> → <span class="user-colored" style="color:…">
        const span = document.createElement('span');
        const colorAttr = el.getAttribute('color');
        if (colorAttr) span.style.color = colorAttr;
        const existingStyle = el.getAttribute('style');
        if (existingStyle) span.setAttribute('style', `${span.getAttribute('style') || ''};${existingStyle}`);
        span.classList.add('user-colored');
        while (el.firstChild) span.appendChild(el.firstChild);
        el.replaceWith(span);
      } else {
        const style = el.getAttribute('style') || '';
        if (/(^|;)\s*color\s*:/i.test(style) || /background(-color)?\s*:/i.test(style)) {
          (el as HTMLElement).classList.add('user-colored');
        }
      }
    });
  };

  // Apply color to selected text
  const applyColor = (type: 'text' | 'highlight', color: string) => {
    if (!editorRef.current) return;

    // Focus the editor first
    editorRef.current.focus();

    // Restore saved selection
    const savedRange = savedSelectionRef.current;
    if (savedRange) {
      try {
        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(savedRange);
        }
      } catch {
        return;
      }
    } else {
      return;
    }

    // Use execCommand for color - it works better with contentEditable
    try {
      // Emit CSS (<span style="color">) instead of legacy <font> tags.
      try { document.execCommand('styleWithCSS', false, 'true'); } catch { /* not all browsers */ }
      if (type === 'text') {
        document.execCommand('foreColor', false, color);
      } else {
        document.execCommand('hiliteColor', false, color);
      }

      // Tag the produced color markup so it survives the save-time heading
      // cleanup (which otherwise drops class-less color spans in headings).
      markUserColoredSpansInSelection();

      // Clear saved selection after applying
      savedSelectionRef.current = null;
    } catch {
      // Silently handle error
    }

    handleInput();
  };

  // Apply line spacing to selected text or current paragraph
  const applyLineSpacing = (spacing: string) => {
    if (!editorRef.current) return;

    editorRef.current.focus();
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      toast.error('Please select text or place cursor in a paragraph');
      return;
    }

    const range = selection.getRangeAt(0);

    // Save state for undo
    saveToHistory();

    // Check if there's actual selected text (not just cursor)
    if (!range.collapsed) {
      // Text is selected - wrap it in a span with line-height
      const selectedText = range.extractContents();
      const span = document.createElement('span');
      span.style.lineHeight = spacing;
      span.style.display = 'inline-block';
      span.appendChild(selectedText);
      range.insertNode(span);

      // Re-select the wrapped content
      selection.removeAllRanges();
      const newRange = document.createRange();
      newRange.selectNodeContents(span);
      selection.addRange(newRange);

      handleInput();
      return;
    }

    // Cursor is placed (no selection) - find and apply to containing block
    let node: Node | null = range.commonAncestorContainer;

    // If it's a text node, get its parent
    if (node.nodeType === Node.TEXT_NODE) {
      node = node.parentElement;
    }

    // Find the closest block element (but not the editor itself)
    let blockElement: HTMLElement | null = node as HTMLElement;
    const blockTags = ['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li', 'blockquote', 'pre'];

    while (blockElement && blockElement !== editorRef.current) {
      const tagName = blockElement.tagName?.toLowerCase();
      if (blockTags.includes(tagName)) {
        // Make sure this is a direct content block, not a component wrapper
        if (!blockElement.classList.contains('learning-objectives') &&
            !blockElement.classList.contains('key-takeaways') &&
            !blockElement.classList.contains('learning-checkpoint') &&
            !blockElement.classList.contains('example-box') &&
            !blockElement.classList.contains('skillzen-code-snippet')) {
          break;
        }
      }
      blockElement = blockElement.parentElement;
    }

    if (blockElement && blockElement !== editorRef.current) {
      // Apply line-height to the block element
      blockElement.style.lineHeight = spacing;
      handleInput();
      toast.success(`Line spacing set to ${spacing}`);
    } else {
      toast.error('Please place cursor in a paragraph to change line spacing');
    }
  };

  // Execute formatting commands
  const execCommand = (command: string, value?: string) => {
    // Ensure editor has focus before executing command
    if (editorRef.current && document.activeElement !== editorRef.current) {
      editorRef.current.focus();
    }

    // For color commands, use applyColor with selection restoration
    if (command === 'foreColor') {
      applyColor('text', value || '#000000');
      return;
    }
    if (command === 'hiliteColor' || command === 'backColor') {
      applyColor('highlight', value || 'transparent');
      return;
    }

    // For list commands, ensure we have a valid selection in the editor
    if (command === 'insertOrderedList' || command === 'insertUnorderedList') {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0 || !editorRef.current?.contains(selection.anchorNode)) {
        // If no selection in editor, place cursor at end
        if (editorRef.current) {
          const range = document.createRange();
          range.selectNodeContents(editorRef.current);
          range.collapse(false);
          selection?.removeAllRanges();
          selection?.addRange(range);
        }
      }
    }

    // For other commands, use execCommand
    document.execCommand(command, false, value);
    handleInput();

    // Update formatting state after command
    setTimeout(checkFormattingState, 0);
  };

  // Insert HTML at cursor position
  const insertHTMLAtCursor = (html: string) => {
    if (!editorRef.current) return;

    // Save state before insertion for undo support
    saveToHistory();

    editorRef.current.focus();

    // Try to restore saved selection first (for when focus was lost to modal/dropdown)
    const savedRange = savedSelectionRef.current;
    if (savedRange) {
      try {
        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(savedRange);
        }
      } catch {
        // If restoration fails, will fall back to append at end
      }
    }

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      // No selection, append at end
      editorRef.current.innerHTML += html;
    } else {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      const fragment = range.createContextualFragment(html);
      range.insertNode(fragment);

      // Move cursor after inserted content
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }

    // Clear saved selection after use
    savedSelectionRef.current = null;
    handleInput();
  };

  // Handle image upload
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image size must be less than 10MB');
      return;
    }

    try {
      setUploadingImage(true);
      toast.loading('Uploading image...', { id: 'image-upload' });

      // Upload to S3 via storage service
      const response = await storageService.uploadFile(file);
      const imageUrl = response.url;

      // Insert resizable image at cursor (click to resize with drag handles)
      const imageHTML = `<p style="text-align: center; margin: 20px auto;">
  <img
    src="${imageUrl}"
    alt="Uploaded image"
    class="content-image"
    style="max-width: 100%; height: auto; display: block; margin: 0 auto; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);"
    loading="lazy"
  />
</p>`;
      insertHTMLAtCursor(imageHTML);

      toast.success('Image uploaded! Click to resize with drag handles', { id: 'image-upload' });
    } catch (error) {
      console.error('Image upload failed:', error);
      toast.error('Failed to upload image', { id: 'image-upload' });
    } finally {
      setUploadingImage(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle audio upload
  const handleAudioUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('audio/')) {
      toast.error('Please select an audio file');
      return;
    }

    // Validate file size (10MB max for audio)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Audio size must be less than 10MB');
      return;
    }

    try {
      setUploadingAudio(true);
      toast.loading('Uploading audio...', { id: 'audio-upload' });

      // Upload to S3 via storage service
      const response = await storageService.uploadFile(file);
      const audioUrl = response.url;

      // Insert audio player at cursor
      const audioHTML = `<div style="margin: 20px auto; max-width: 100%;">
  <audio
    controls
    controlslist="nodownload"
    preload="metadata"
    style="width: 100%; border-radius: 8px;"
  >
    <source src="${audioUrl}" type="${file.type}" />
    Your browser does not support the audio element.
  </audio>
</div>`;
      insertHTMLAtCursor(audioHTML);

      toast.success('Audio uploaded successfully!', { id: 'audio-upload' });
    } catch (error) {
      console.error('Audio upload failed:', error);
      toast.error('Failed to upload audio', { id: 'audio-upload' });
    } finally {
      setUploadingAudio(false);
      setShowAudioDropdown(false);
      if (audioInputRef.current) {
        audioInputRef.current.value = '';
      }
    }
  };

  // Handle video upload
  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // MP4 only. `accept` on the input is just a dialog filter (the user can switch to
    // "All files"), so enforce it here too. Some OSes report a blank/odd mime type for
    // .mp4, hence the extension fallback.
    const is_mp4 = file.type === 'video/mp4' || /\.mp4$/i.test(file.name);
    if (!is_mp4) {
      toast.error('Only MP4 videos are supported. Please convert the file to MP4 and try again.');
      return;
    }

    // Validate file size (500MB max for video)
    if (file.size > 500 * 1024 * 1024) {
      toast.error('Video size must be less than 500MB');
      return;
    }

    try {
      setUploadingVideo(true);
      toast.loading('Uploading video...', { id: 'video-upload' });

      // Upload to S3 via storage service
      const response = await storageService.uploadFile(file);
      const videoUrl = response.url;

      // Insert video player at cursor
      const videoHTML = `<div style="margin: 20px auto; max-width: 100%;">
  <video
    controls
    controlslist="nodownload"
    disablepictureinpicture
    preload="metadata"
    style="width: 100%; max-width: 800px; display: block; margin: 0 auto; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.15);"
  >
    <source src="${videoUrl}" type="video/mp4" />
    Your browser does not support the video element.
  </video>
</div>`;
      insertHTMLAtCursor(videoHTML);

      toast.success('Video uploaded successfully!', { id: 'video-upload' });
    } catch (error) {
      console.error('Video upload failed:', error);
      toast.error('Failed to upload video', { id: 'video-upload' });
    } finally {
      setUploadingVideo(false);
      setShowVideoDropdown(false);
      if (videoInputRef.current) {
        videoInputRef.current.value = '';
      }
    }
  };

  // Open media insert modal
  const openMediaInsertModal = (type: MediaInsertType) => {
    // Save current selection BEFORE opening modal (so we can restore it when inserting)
    saveSelection();
    setMediaInsertType(type);
    setMediaInsertModalOpen(true);
    setShowImageDropdown(false);
    setShowAudioDropdown(false);
    setShowVideoDropdown(false);
  };

  // Handle media insert from modal
  const handleMediaInsert = (html: string) => {
    // Restore saved selection before inserting
    if (editorRef.current) {
      editorRef.current.focus();

      // Try to restore the saved selection
      const savedRange = savedSelectionRef.current;
      if (savedRange) {
        try {
          const selection = window.getSelection();
          if (selection) {
            selection.removeAllRanges();
            selection.addRange(savedRange);
          }
        } catch {
          // If restoration fails, insert at end
        }
      }
    }

    insertHTMLAtCursor(html);
    savedSelectionRef.current = null; // Clear saved selection after use
    toast.success('Media inserted successfully!');
  };

  // Convert emoji to HTML entity to prevent encoding issues
  const emojiToEntity = (emoji: string): string => {
    let result = '';
    for (const char of emoji) {
      const codePoint = char.codePointAt(0);
      result += codePoint ? `&#${codePoint};` : char;
    }
    return result;
  };

  // Insert emoji at cursor (as HTML entity for encoding safety)
  const insertEmoji = (emoji: string) => {
    if (!editorRef.current) return;

    // Save state before insertion for undo support
    saveToHistory();

    const emojiEntity = emojiToEntity(emoji);
    const savedRange = savedSelectionRef.current;

    // Restore selection and insert at saved position
    if (savedRange) {
      editorRef.current.focus();
      try {
        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(savedRange);
          // Insert emoji at the restored cursor position
          document.execCommand('insertHTML', false, emojiEntity);
        }
      } catch {
        // Fallback: append at end
        editorRef.current.innerHTML += emojiEntity;
      }
    } else {
      // No saved selection, append at end
      editorRef.current.focus();
      editorRef.current.innerHTML += emojiEntity;
    }

    handleInput();
    setShowEmojiPicker(false);
    savedSelectionRef.current = null;
  };

  // Insert table
  const insertTable = (rows: number, cols: number) => {
    let tableHTML = '<table style="border-collapse: collapse; width: 100%; margin: 20px 0; border: 2px solid #e5e7eb;">';
    tableHTML += '<tbody>';
    for (let i = 0; i < rows; i++) {
      tableHTML += '<tr>';
      for (let j = 0; j < cols; j++) {
        tableHTML += '<td style="border: 1px solid #d1d5db; padding: 8px; min-width: 100px;">';
        tableHTML += i === 0 ? `<strong>Header ${j + 1}</strong>` : `Cell ${i}-${j + 1}`;
        tableHTML += '</td>';
      }
      tableHTML += '</tr>';
    }
    tableHTML += '</tbody>';
    tableHTML += '</table>';
    insertHTMLAtCursor(tableHTML);
    setShowTableModal(false);
    toast.success('Table inserted! Click cells to edit');
  };

  // Insert hyperlink
  const insertLink = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      toast.error('Please select text first');
      return;
    }

    const selectedText = selection.toString();
    if (selectedText) {
      setLinkText(selectedText);
    }
    setShowLinkModal(true);
  };

  const confirmInsertLink = () => {
    if (!linkUrl) {
      toast.error('Please enter a URL');
      return;
    }

    const url = linkUrl.startsWith('http') ? linkUrl : `https://${linkUrl}`;
    const text = linkText || url;

    const linkHTML = `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color: #3b82f6; text-decoration: underline;">${text}</a>`;
    insertHTMLAtCursor(linkHTML);

    setShowLinkModal(false);
    setLinkUrl('');
    setLinkText('');
    toast.success('Link inserted!');
  };

  // Code language configurations
  const CODE_LANGUAGES = [
    { value: 'javascript', label: 'JavaScript', emoji: '💻', sample: '// Your code here\nfunction example() {\n  console.log("Hello, World!");\n  return true;\n}' },
    { value: 'python', label: 'Python', emoji: '🐍', sample: '# Your code here\ndef example():\n    print("Hello, World!")\n    return True' },
    { value: 'html', label: 'HTML', emoji: '🌐', sample: '<!-- Your HTML here -->\n<div class="container">\n  <h1>Hello, World!</h1>\n</div>' },
    { value: 'css', label: 'CSS', emoji: '🎨', sample: '/* Your styles here */\n.container {\n  color: #333;\n  font-size: 16px;\n}' },
    { value: 'typescript', label: 'TypeScript', emoji: '🔷', sample: '// Your code here\nfunction example(): boolean {\n  console.log("Hello, World!");\n  return true;\n}' },
    { value: 'java', label: 'Java', emoji: '☕', sample: '// Your code here\npublic class Example {\n  public static void main(String[] args) {\n    System.out.println("Hello, World!");\n  }\n}' },
    { value: 'csharp', label: 'C#', emoji: '🔶', sample: '// Your code here\nusing System;\npublic class Example {\n  static void Main() {\n    Console.WriteLine("Hello, World!");\n  }\n}' },
    { value: 'php', label: 'PHP', emoji: '🐘', sample: '<?php\n// Your code here\nfunction example() {\n  echo "Hello, World!";\n  return true;\n}\n?>' },
    { value: 'sql', label: 'SQL', emoji: '🗄️', sample: '-- Your query here\nSELECT * FROM users\nWHERE status = \'active\'\nORDER BY created_at DESC;' },
    { value: 'bash', label: 'Bash', emoji: '🖥️', sample: '#!/bin/bash\n# Your script here\necho "Hello, World!"\nexit 0' },
  ];

  // Insert code block with language selector (matches SDK skillzen-code-snippet format)
  const insertCodeBlock = (languageKey: string = 'javascript') => {
    const language = CODE_LANGUAGES.find(l => l.value === languageKey) || CODE_LANGUAGES[0];

    // Match SDK format from HtmlThemeTemplates.ts and ContentGenerationPrompts.ts
    // Note: No contenteditable attributes - code is editable via parent contenteditable div
    // This ensures code blocks are NOT editable in preview/PDF mode
    // Inline styles ensure proper display in both editor and preview
    const codeHTML = `<div class="skillzen-code-snippet" data-language="${language.value}" style="background: #1e293b; border: 2px solid #374151; border-radius: 12px; margin: 24px 0; box-shadow: 0 4px 12px rgba(0,0,0,0.15); overflow: hidden; font-family: 'Fira Code', 'SF Mono', 'Monaco', 'Consolas', monospace;">
  <div class="skillzen-code-header" style="background: linear-gradient(135deg, #374151 0%, #4b5563 100%); color: #f3f4f6; padding: 12px 20px; font-weight: 600; display: flex; align-items: center; gap: 8px; border-bottom: 1px solid #4b5563;">
    <span class="language-emoji">${language.emoji}</span>
    <span class="language-label">${language.label.toUpperCase()}</span>
    <span style="margin-left: auto; font-size: 10px; opacity: 0.7;">Code</span>
  </div>
  <div class="skillzen-code-content" style="padding: 20px; font-size: 14px; line-height: 1.6; overflow-x: auto; background: #1e293b;">
    <pre style="margin: 0; background: transparent; border: none; padding: 0;"><code style="color: #e5e7eb; background: transparent; border: none; font-family: inherit;">${escapeHtml(language.sample)}</code></pre>
  </div>
</div>`;
    insertHTMLAtCursor(codeHTML);
    toast.success(`${language.emoji} ${language.label} code block inserted! Click inside to edit code.`);
  };

  // Show code language selector modal
  const [showCodeLanguageModal, setShowCodeLanguageModal] = useState(false);

  const handleInsertCodeWithLanguage = () => {
    setShowCodeLanguageModal(true);
  };

  // Find text in editor
  const handleFind = () => {
    if (!editorRef.current || !findText) return;

    const content = editorRef.current.innerText;
    const searchFlags = caseSensitive ? 'g' : 'gi';
    const regex = new RegExp(findText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), searchFlags);
    const matches = content.match(regex);

    setFindMatches(matches ? matches.length : 0);

    // Highlight matches using browser find
    if ((window as any).find) {
      (window as any).find(findText, caseSensitive, false, true);
    }

    if (matches && matches.length > 0) {
      toast.success(`Found ${matches.length} match${matches.length > 1 ? 'es' : ''}`);
    } else {
      toast.error('No matches found');
    }
  };

  // Replace current match
  const handleReplace = () => {
    if (!editorRef.current || !findText) return;

    const selection = window.getSelection();
    if (selection && selection.toString().toLowerCase() === findText.toLowerCase()) {
      document.execCommand('insertText', false, replaceText);
      handleInput();
      toast.success('Replaced 1 occurrence');
      // Find next
      setTimeout(handleFind, 100);
    } else {
      toast.error('Please find text first');
    }
  };

  // Replace all occurrences
  const handleReplaceAll = () => {
    if (!editorRef.current || !findText) return;

    let content = editorRef.current.innerHTML;
    const searchFlags = caseSensitive ? 'g' : 'gi';
    const regex = new RegExp(findText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), searchFlags);
    const matches = content.match(regex);

    if (matches && matches.length > 0) {
      content = content.replace(regex, replaceText);
      editorRef.current.innerHTML = content;
      handleInput();
      toast.success(`Replaced ${matches.length} occurrence${matches.length > 1 ? 's' : ''}`);
      setShowFindReplace(false);
    } else {
      toast.error('No matches found');
    }
  };

  // Insert inline code (matches SDK format - simple code tag)
  const insertInlineCode = () => {
    const selection = window.getSelection();
    if (selection && selection.toString()) {
      const selectedText = selection.toString();
      // Use simple <code> tag - styling comes from theme CSS
      const codeHTML = `<code>${selectedText}</code>`;
      insertHTMLAtCursor(codeHTML);
      toast.success('Inline code applied!');
    } else {
      toast.error('Please select text first');
    }
  };

  // Insert blockquote
  const insertBlockquote = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      toast.error('Please select text or place cursor in a paragraph');
      return;
    }

    // Ensure editor has focus
    editorRef.current?.focus();

    // Use angle brackets for cross-browser compatibility
    document.execCommand('formatBlock', false, '<blockquote>');

    // Apply custom styles to the blockquote
    // Need to get the element from the node (anchorNode is often a text node)
    const node = selection.anchorNode;
    const element = node?.nodeType === Node.TEXT_NODE ? node.parentElement : node as HTMLElement;
    if (element) {
      const blockquote = element.closest('blockquote');
      if (blockquote) {
        blockquote.setAttribute('style', 'border-left: 4px solid #3b82f6; padding-left: 16px; margin: 20px 0; font-style: italic; color: #6b7280; background-color: #f9fafb;');
      }
    }

    handleInput();
    toast.success('Blockquote inserted!');
  };

  // Insert horizontal rule
  const insertHorizontalRule = () => {
    const hrHTML = '<hr style="border: none; border-top: 2px solid #e5e7eb; margin: 24px 0;" />';
    insertHTMLAtCursor(hrHTML);
    toast.success('Divider inserted!');
  };

  // Notify parent of content change (for component edits)
  const notifyChange = () => {
    if (editorRef.current && fullHTMLStructure) {
      let editedBodyContent = editorRef.current.innerHTML;
      editedBodyContent = fixEmojiEncoding(editedBodyContent);

      const fullHTML = `${fullHTMLStructure.doctype}
${fullHTMLStructure.htmlTag}
<head>${fullHTMLStructure.headContent}</head>
${fullHTMLStructure.bodyTag}
${editedBodyContent}
${fullHTMLStructure.scripts}
</body>
</html>`;

      onChange(fullHTML);
    }
  };

  // Open component configuration modal
  const openComponentModal = (type: ComponentType) => {
    setComponentModalType(type);
    setComponentModalOpen(true);
  };

  // Handle component insertion from modal
  const handleComponentInsert = (html: string) => {
    // Save state before component change for undo support
    saveToHistory();

    if (editingComponentElement) {
      // Editing existing component - replace it with all generated HTML (including style tags)
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;

      // Get all child nodes (includes both the component div and style tags)
      const fragment = document.createDocumentFragment();
      while (tempDiv.firstChild) {
        fragment.appendChild(tempDiv.firstChild);
      }

      if (editingComponentElement.parentNode) {
        // Clean up old style tag if it exists (look for style tag with data-for matching component id)
        const oldComponentId = editingComponentElement.id;
        if (oldComponentId) {
          const oldStyleTag = document.querySelector(`style[data-for="${oldComponentId}"]`);
          if (oldStyleTag) {
            oldStyleTag.remove();
          }
        }

        // Insert all new content before the old element, then remove the old element
        editingComponentElement.parentNode.insertBefore(fragment, editingComponentElement);
        editingComponentElement.remove();
        notifyChange();
        toast.success('Component updated successfully');
      }

      setEditingComponentElement(null);
    } else {
      // Inserting new component
      insertHTMLAtCursor(html);
      toast.success('Component inserted successfully');
    }
    setComponentModalOpen(false);
  };

  // Handle editing an existing component
  const handleEditComponent = (element: HTMLElement, type: string) => {
    setEditingComponentElement(element);

    // Determine component type for modal
    if (type === 'Knowledge Check') {
      setComponentModalType('knowledge-check');
    } else if (type === 'Learning Objectives') {
      setComponentModalType('learning-objectives');
    } else if (type === 'Key Takeaways') {
      setComponentModalType('key-takeaways');
    } else if (type === 'Example Box') {
      setComponentModalType('example-box');
    } else if (type === 'Tip Callout') {
      setComponentModalType('callout-tip');
    } else if (type === 'Warning Callout') {
      setComponentModalType('callout-warning');
    } else if (type === 'Note Callout') {
      setComponentModalType('callout-note');
    } else if (type === 'Info Callout') {
      setComponentModalType('callout-info');
    } else {
      // Default to knowledge check for unknown types
      setComponentModalType('knowledge-check');
    }

    setComponentModalOpen(true);
  };


  // Handle right-click on tables for context menu
  const handleEditorContextMenu = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const table = target.closest('table');
    const cell = target.closest('td, th') as HTMLTableCellElement;

    if (table && cell) {
      e.preventDefault();
      setSelectedTableElement(table as HTMLTableElement);
      setSelectedTableCell(cell);
      setTableContextMenuPosition({ x: e.clientX, y: e.clientY });
      setShowTableContextMenu(true);
    }
  };

  // Table editing functions
  const addTableRow = (position: 'above' | 'below') => {
    if (!selectedTableCell || !selectedTableElement) return;

    // Save state before table change for undo support
    saveToHistory();

    const row = selectedTableCell.parentElement as HTMLTableRowElement;
    const newRow = row.cloneNode(true) as HTMLTableRowElement;

    // Clear cell contents
    newRow.querySelectorAll('td, th').forEach((cell, index) => {
      cell.textContent = `Cell ${index + 1}`;
    });

    if (position === 'above') {
      row.parentElement?.insertBefore(newRow, row);
    } else {
      row.parentElement?.insertBefore(newRow, row.nextSibling);
    }

    handleInput();
    setShowTableContextMenu(false);
    toast.success('Row added!');
  };

  const addTableColumn = (position: 'left' | 'right') => {
    if (!selectedTableCell || !selectedTableElement) return;

    // Save state before table change for undo support
    saveToHistory();

    const cellIndex = Array.from(selectedTableCell.parentElement!.children).indexOf(selectedTableCell);
    const rows = selectedTableElement.querySelectorAll('tr');

    rows.forEach((row) => {
      const newCell = document.createElement(row.querySelector('th') ? 'th' : 'td');
      newCell.textContent = 'New Cell';
      newCell.setAttribute('style', 'border: 1px solid #d1d5db; padding: 8px; min-width: 100px;');

      const targetCell = row.children[cellIndex];
      if (position === 'left') {
        row.insertBefore(newCell, targetCell);
      } else {
        row.insertBefore(newCell, targetCell.nextSibling);
      }
    });

    handleInput();
    setShowTableContextMenu(false);
    toast.success('Column added!');
  };

  const deleteTableRow = () => {
    if (!selectedTableCell) return;

    const row = selectedTableCell.parentElement as HTMLTableRowElement;
    const table = row.closest('table');
    const rowCount = table?.querySelectorAll('tr').length || 0;

    if (rowCount <= 1) {
      toast.error('Cannot delete the last row. Delete the entire table instead.');
      return;
    }

    // Save state before delete for undo support
    saveToHistory();

    row.remove();
    handleInput();
    setShowTableContextMenu(false);
    toast.success('Row deleted!');
  };

  const deleteTableColumn = () => {
    if (!selectedTableCell || !selectedTableElement) return;

    const cellIndex = Array.from(selectedTableCell.parentElement!.children).indexOf(selectedTableCell);
    const rows = selectedTableElement.querySelectorAll('tr');
    const colCount = rows[0]?.children.length || 0;

    if (colCount <= 1) {
      toast.error('Cannot delete the last column. Delete the entire table instead.');
      return;
    }

    // Save state before delete for undo support
    saveToHistory();

    rows.forEach((row) => {
      row.children[cellIndex]?.remove();
    });

    handleInput();
    setShowTableContextMenu(false);
    toast.success('Column deleted!');
  };

  const deleteTable = () => {
    if (!selectedTableElement) return;

    if (confirm('Delete this entire table?')) {
      // Save state before delete for undo support
      saveToHistory();
      selectedTableElement.remove();
      handleInput();
      setShowTableContextMenu(false);
      toast.success('Table deleted!');
    }
  };

  // Handle click on editor (for general interactions)
  const handleEditorClick = (_e: React.MouseEvent) => {
    // Close table context menu when clicking elsewhere
    if (showTableContextMenu) {
      setShowTableContextMenu(false);
    }
  };

  // Handle double-click on editor (detect component clicks for editing)
  const handleEditorDoubleClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;

    // Only detect pedagogical components that should open edit modals on double-click
    // Code blocks, tables, and other content should be directly editable
    const component = target.closest('.learning-checkpoint, .learning-objectives, .key-takeaways');

    if (component) {
      // Check if user is clicking on interactive elements within component (buttons, links)
      const isInteractiveElement = target.closest('button, a, input, select, textarea');
      if (isInteractiveElement) {
        // Allow interactive elements to work normally
        return;
      }

      // Prevent editing the component text directly
      e.preventDefault();

      // Determine component type
      let typeName = 'Component';

      if (component.classList.contains('learning-checkpoint')) {
        typeName = 'Knowledge Check';
      } else if (component.classList.contains('learning-objectives')) {
        typeName = 'Learning Objectives';
      } else if (component.classList.contains('key-takeaways')) {
        typeName = 'Key Takeaways';
      } else if (component.classList.contains('example-box')) {
        typeName = 'Example Box';
      } else if (component.classList.contains('callout-tip') || component.getAttribute('data-component') === 'callout-tip') {
        typeName = 'Tip Callout';
      } else if (component.classList.contains('callout-warning') || component.getAttribute('data-component') === 'callout-warning') {
        typeName = 'Warning Callout';
      } else if (component.classList.contains('callout-note') || component.getAttribute('data-component') === 'callout-note') {
        typeName = 'Note Callout';
      } else if (component.classList.contains('callout-info') || component.getAttribute('data-component') === 'callout-info') {
        typeName = 'Info Callout';
      }

      // Open edit modal
      handleEditComponent(component as HTMLElement, typeName);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Don't close if clicking inside the dropdowns
      if (target.closest('.color-picker-dropdown') ||
          target.closest('.emoji-picker-dropdown') ||
          target.closest('.media-dropdown')) {
        return;
      }
      // Don't close if clicking on the toggle buttons
      if (target.closest('[data-color-toggle]') || target.closest('[data-media-toggle]')) {
        return;
      }
      setShowColorPicker(false);
      setShowBgColorPicker(false);
      setShowEmojiPicker(false);
      setShowImageDropdown(false);
      setShowAudioDropdown(false);
      setShowVideoDropdown(false);
    };

    if (showColorPicker || showBgColorPicker || showEmojiPicker || showImageDropdown || showAudioDropdown || showVideoDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showColorPicker, showBgColorPicker, showEmojiPicker, showImageDropdown, showAudioDropdown, showVideoDropdown]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Save - Ctrl+S
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        e.stopPropagation();
        if (onSave) {
          onSave();
        }
      }

      // Show keyboard shortcuts - Ctrl+/
      if ((e.ctrlKey || e.metaKey) && e.key === '/') {
        e.preventDefault();
        setShowKeyboardShortcuts(true);
      }

      // Find - Ctrl+F
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        setShowFindReplace(true);
      }

      // Replace - Ctrl+H
      if ((e.ctrlKey || e.metaKey) && e.key === 'h') {
        e.preventDefault();
        setShowFindReplace(true);
      }

      // Custom Undo - Ctrl+Z
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        // Only intercept if we have custom history
        if (historyStackRef.current.length > 0 && historyIndexRef.current > 0) {
          e.preventDefault();
          handleUndo();
        }
        // Otherwise, let the browser handle native undo
      }

      // Custom Redo - Ctrl+Y or Ctrl+Shift+Z
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        // Only intercept if we have custom history to redo
        if (historyStackRef.current.length > 0 && historyIndexRef.current < historyStackRef.current.length - 1) {
          e.preventDefault();
          handleRedo();
        }
        // Otherwise, let the browser handle native redo
      }

      // Close pickers on Escape key
      if (e.key === 'Escape') {
        if (showKeyboardShortcuts) {
          setShowKeyboardShortcuts(false);
        } else if (showFindReplace) {
          setShowFindReplace(false);
        } else {
          setShowColorPicker(false);
          setShowBgColorPicker(false);
          setShowEmojiPicker(false);
          setShowTableModal(false);
          setShowLinkModal(false);
          setShowCodeLanguageModal(false);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onSave, showKeyboardShortcuts, handleUndo, handleRedo]);

  // Intercept component delete button clicks to save history before deletion
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || readOnly) return;

    const handleDeleteButtonClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Check if clicked element is a component delete button
      if (target.classList.contains('component-delete-btn') || target.closest('.component-delete-btn')) {
        // Save state before delete for undo support
        saveToHistory();
        // The inline onclick will handle the actual deletion
        // After deletion, trigger content change
        setTimeout(() => {
          handleInput();
        }, 10);
      }
    };

    // Use capture phase to intercept before the inline onclick
    editor.addEventListener('click', handleDeleteButtonClick, true);
    return () => editor.removeEventListener('click', handleDeleteButtonClick, true);
  }, [readOnly, saveToHistory, handleInput]);

  // Listen for selection changes to update toolbar button states and save selection
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || readOnly) return;

    // Check formatting and save selection on selection change
    const handleSelectionChange = () => {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0 && editor.contains(selection.anchorNode)) {
        checkFormattingState();
        // Auto-save selection for color picker use
        const range = selection.getRangeAt(0);
        if (!range.collapsed) {
          savedSelectionRef.current = range.cloneRange();
        }
      }
    };

    // Check formatting on keyup (for cursor movement with arrow keys)
    const handleKeyUp = () => {
      checkFormattingState();
    };

    // Check formatting and save selection on mouseup (for mouse selections)
    const handleMouseUp = () => {
      checkFormattingState();
      // Save selection after mouse selection
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0 && editor.contains(selection.anchorNode)) {
        const range = selection.getRangeAt(0);
        if (!range.collapsed) {
          savedSelectionRef.current = range.cloneRange();
        }
      }
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    editor.addEventListener('keyup', handleKeyUp);
    editor.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
      editor.removeEventListener('keyup', handleKeyUp);
      editor.removeEventListener('mouseup', handleMouseUp);
    };
  }, [readOnly, checkFormattingState]);

  return (
    <div className={`border border-gray-300 rounded-lg bg-white max-h-[85vh] overflow-y-auto ${className}`} dir="ltr">
      {/* Image Resize Handler - adds Word-like drag handles */}
      {!readOnly && <ImageResizeHandler editorRef={editorRef} onContentChange={handleInput} onBeforeDelete={saveToHistory} />}
      {!readOnly && <MediaControlHandler editorRef={editorRef} onContentChange={handleInput} />}
      {/* Drag and Drop Handler - allows reordering of components and media */}
      {!readOnly && <DragDropHandler editorRef={editorRef} onContentChange={handleInput} onBeforeMove={saveToHistory} />}

      {/* Inject extracted CSS styles */}
      {extractedStyles && <style dangerouslySetInnerHTML={{ __html: extractedStyles }} />}
      {/* Protect toolbar/ribbon from dark-mode content styles leaking */}
      <style dangerouslySetInnerHTML={{ __html: `.visual-editor-toolbar, .visual-editor-toolbar * { direction: ltr !important; unicode-bidi: normal !important; text-align: left !important; } .visual-editor-toolbar { color: #111827 !important; background-color: #fff !important; } .visual-editor-toolbar button, .visual-editor-toolbar span, .visual-editor-toolbar label, .visual-editor-toolbar input, .visual-editor-toolbar select, .visual-editor-toolbar p { color: inherit !important; } .visual-editor-toolbar [class*="text-"] { text-align: inherit !important; }` }} />

      {/* Editor-specific CSS for code blocks and lists - ensures proper styling in editor view */}
      <style dangerouslySetInnerHTML={{ __html: `
        /* List styles - override Tailwind reset (EXCLUDE learning-objectives and key-takeaways) */
        .editor-content-preview ul:not(.learning-objectives ul):not(.key-takeaways ul) {
          list-style-type: disc !important;
          padding-left: 2rem !important;
          margin: 1rem 0 !important;
        }
        .editor-content-preview ol {
          list-style-type: decimal !important;
          padding-left: 2rem !important;
          margin: 1rem 0 !important;
        }
        .editor-content-preview ul ul:not(.learning-objectives ul):not(.key-takeaways ul) {
          list-style-type: circle !important;
        }
        .editor-content-preview ul ul ul:not(.learning-objectives ul):not(.key-takeaways ul) {
          list-style-type: square !important;
        }
        .editor-content-preview ol ol {
          list-style-type: lower-alpha !important;
        }
        .editor-content-preview ol ol ol {
          list-style-type: lower-roman !important;
        }
        .editor-content-preview li:not(.learning-objectives li):not(.key-takeaways li) {
          margin: 0.25rem 0 !important;
          display: list-item !important;
        }
        /* Learning objectives and key-takeaways - respect their own styles */
        .editor-content-preview .learning-objectives ul,
        .editor-content-preview .key-takeaways ul {
          list-style: inherit !important;
        }
        .editor-content-preview .learning-objectives li,
        .editor-content-preview .key-takeaways li {
          display: list-item !important;
          margin: 0.5rem 0 !important;
        }
        /* When custom icons are used, hide bullets but KEEP the ::before icon from component styles */
        .editor-content-preview .learning-objectives[data-use-custom-icon="true"] ul,
        .editor-content-preview .key-takeaways[data-use-custom-icon="true"] ul {
          list-style: none !important;
          padding-left: 0 !important;
          margin: 0 !important;
        }
        .editor-content-preview .learning-objectives[data-use-custom-icon="true"] li,
        .editor-content-preview .key-takeaways[data-use-custom-icon="true"] li {
          list-style: none !important;
          padding-left: 28px !important;
          position: relative !important;
          display: block !important;
          margin-bottom: 8px !important;
        }
        /* Ensure the custom icon ::before is properly positioned */
        .editor-content-preview .learning-objectives[data-use-custom-icon="true"] li::before,
        .editor-content-preview .key-takeaways[data-use-custom-icon="true"] li::before {
          position: absolute !important;
          left: 0 !important;
          top: 0 !important;
          width: 20px !important;
          text-align: center !important;
        }
        /* When bullet mode is used, show bullets and CLEAR any ::before from AI styles */
        .editor-content-preview .learning-objectives[data-use-custom-icon="false"] ul,
        .editor-content-preview .key-takeaways[data-use-custom-icon="false"] ul {
          list-style-type: disc !important;
          padding-left: 20px !important;
        }
        .editor-content-preview .learning-objectives[data-use-custom-icon="false"] li::before,
        .editor-content-preview .key-takeaways[data-use-custom-icon="false"] li::before {
          content: none !important;
          display: none !important;
        }
        .editor-content-preview .learning-objectives[data-use-custom-icon="false"] li,
        .editor-content-preview .key-takeaways[data-use-custom-icon="false"] li {
          list-style-type: disc !important;
          display: list-item !important;
          padding-left: 0 !important;
        }
        .editor-content-preview .skillzen-code-snippet {
          background: #1e293b !important;
          border: 2px solid #374151 !important;
          border-radius: 12px !important;
          margin: 24px 0 !important;
          overflow: hidden !important;
          font-family: 'Fira Code', 'SF Mono', 'Monaco', 'Consolas', monospace !important;
        }
        .editor-content-preview .skillzen-code-header {
          background: linear-gradient(135deg, #374151 0%, #4b5563 100%) !important;
          color: #f3f4f6 !important;
          padding: 12px 20px !important;
          font-weight: 600 !important;
          display: flex !important;
          align-items: center !important;
          gap: 8px !important;
          border-bottom: 1px solid #4b5563 !important;
        }
        .editor-content-preview .skillzen-code-content {
          padding: 20px !important;
          font-size: 14px !important;
          line-height: 1.6 !important;
          overflow-x: auto !important;
          background: #1e293b !important;
        }
        .editor-content-preview .skillzen-code-content pre {
          margin: 0 !important;
          background: transparent !important;
          border: none !important;
          padding: 0 !important;
        }
        .editor-content-preview .skillzen-code-content code {
          color: #e5e7eb !important;
          background: transparent !important;
          border: none !important;
          font-family: 'Fira Code', 'SF Mono', 'Monaco', 'Consolas', monospace !important;
        }
        /* Ensure ALL text inside code blocks uses monospace font */
        .editor-content-preview .skillzen-code-snippet,
        .editor-content-preview .skillzen-code-snippet *,
        .editor-content-preview .skillzen-code-content,
        .editor-content-preview .skillzen-code-content * {
          font-family: 'Fira Code', 'SF Mono', 'Monaco', 'Consolas', monospace !important;
        }
        .editor-content-preview .skillzen-code-header,
        .editor-content-preview .skillzen-code-header * {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
        }
        /* Prevent blue selection on code blocks */
        .editor-content-preview .skillzen-code-snippet::selection,
        .editor-content-preview .skillzen-code-snippet *::selection {
          background: rgba(99, 102, 241, 0.3) !important;
        }
      ` }} />

      {/* Microsoft Word-Style Ribbon Toolbar */}
      {!readOnly && (
        <div className="visual-editor-toolbar border-b-2 border-gray-300 bg-white shadow-md sticky top-0 z-50" dir="ltr">
          {/* Ribbon Tabs - Microsoft Word Style */}
          <div className="flex items-center border-b border-gray-200 bg-gray-50" dir="ltr">
            <button
              type="button"
              onClick={() => setActiveRibbonTab('home')}
              className={`px-6 py-2 text-sm font-semibold transition-colors relative ${
                activeRibbonTab === 'home'
                  ? 'text-brand-blue-600 bg-white border-b-2 border-brand-blue-600'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Home
            </button>
            {!hideInsertTab && (
              <button
                type="button"
                onClick={() => setActiveRibbonTab('insert')}
                className={`px-6 py-2 text-sm font-semibold transition-colors relative ${
                  activeRibbonTab === 'insert'
                    ? 'text-brand-blue-600 bg-white border-b-2 border-brand-blue-600'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Insert
              </button>
            )}
            {!hideFormatTab && (
              <button
                type="button"
                onClick={() => setActiveRibbonTab('format')}
                className={`px-6 py-2 text-sm font-semibold transition-colors relative ${
                  activeRibbonTab === 'format'
                    ? 'text-brand-blue-600 bg-white border-b-2 border-brand-blue-600'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Format
              </button>
            )}
            <div className="ml-auto px-4 flex items-center gap-2 text-[10px] text-gray-500">
              <kbd className="px-1.5 py-0.5 bg-gray-200 rounded">Ctrl+S</kbd>
              <kbd className="px-1.5 py-0.5 bg-gray-200 rounded">Ctrl+F</kbd>
              <button
                type="button"
                onClick={() => setShowKeyboardShortcuts(true)}
                className="px-2 py-1 bg-brand-blue-100 text-brand-blue-700 rounded hover:bg-brand-blue-200 font-semibold"
              >
                ⌨️
              </button>
            </div>
          </div>

          {/* HOME TAB CONTENT */}
          {activeRibbonTab === 'home' && (
            <div className="p-3 bg-white">
              {/* Font Group */}
              <div className="inline-flex flex-col border-r border-gray-200 pr-3 mr-3">
                <div className="text-[9px] font-semibold text-gray-500 uppercase mb-1 px-1">Font</div>
                <div className="flex flex-wrap items-center gap-2">
            {/* Font Family */}
            <select
              value={activeFormats.fontName || ''}
              onChange={(e) => {
                if (e.target.value) {
                  // For System Default, use the system font stack
                  if (e.target.value === 'System Default') {
                    execCommand('fontName', "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif");
                  } else {
                    execCommand('fontName', e.target.value);
                  }
                }
              }}
              className="px-3 py-2 text-sm font-medium border-2 rounded-lg bg-white hover:border-brand-blue-400 hover:bg-brand-blue-50 focus:outline-none focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500 transition-all cursor-pointer shadow-sm hover:shadow-md min-w-[140px] border-gray-300"
            >
              <option value="">{activeFormats.fontName || 'Font Family'}</option>
              <option value="System Default">System Default</option>
              <option value="Arial">Arial</option>
              <option value="Times New Roman">Times New Roman</option>
              <option value="Courier New">Courier New</option>
              <option value="Georgia">Georgia</option>
              <option value="Verdana">Verdana</option>
              <option value="Tahoma">Tahoma</option>
              <option value="Trebuchet MS">Trebuchet MS</option>
              <option value="Comic Sans MS">Comic Sans MS</option>
            </select>

            {/* Font Size. Deliberately not bound to the caret: queryCommandValue reports
                the legacy 1-7 bucket, so the label flipped between 12pt/18pt/36pt as the
                caret moved, and leaked a raw "Size 3" for anything outside 1-7. Apply-only
                menu — it always falls back to the placeholder. */}
            <select
              value=""
              onChange={(e) => {
                if (e.target.value) {
                  execCommand('fontSize', e.target.value);
                }
              }}
              className="px-3 py-2 text-sm font-medium border-2 rounded-lg bg-white hover:border-brand-blue-400 hover:bg-brand-blue-50 focus:outline-none focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500 transition-all cursor-pointer shadow-sm hover:shadow-md min-w-[100px] border-gray-300"
            >
              <option value="">Size</option>
              <option value="1">8pt</option>
              <option value="2">10pt</option>
              <option value="3">12pt</option>
              <option value="4">14pt</option>
              <option value="5">18pt</option>
              <option value="6">24pt</option>
              <option value="7">36pt</option>
            </select>

            {/* Heading Style */}
            <select
              value={activeFormats.formatBlock || ''}
              onChange={(e) => {
                if (e.target.value) {
                  execCommand('formatBlock', e.target.value);
                }
              }}
              className="px-3 py-2 text-sm font-medium border-2 rounded-lg bg-white hover:border-brand-blue-400 hover:bg-brand-blue-50 focus:outline-none focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500 transition-all cursor-pointer shadow-sm hover:shadow-md min-w-[120px] border-gray-300"
            >
              <option value="">
                {activeFormats.formatBlock
                  ? activeFormats.formatBlock === 'p' ? 'Normal'
                    : activeFormats.formatBlock.toUpperCase()
                  : 'Text Style'}
              </option>
              <option value="p">Normal</option>
              <option value="h1">Heading 1</option>
              <option value="h2">Heading 2</option>
              <option value="h3">Heading 3</option>
              <option value="h4">Heading 4</option>
              <option value="h5">Heading 5</option>
              <option value="h6">Heading 6</option>
            </select>

            {/* Line Spacing */}
            <select
              onChange={(e) => {
                if (e.target.value) {
                  applyLineSpacing(e.target.value);
                }
              }}
              className="px-3 py-2 text-sm font-medium border-2 rounded-lg bg-white hover:border-brand-blue-400 hover:bg-brand-blue-50 focus:outline-none focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500 transition-all cursor-pointer shadow-sm hover:shadow-md min-w-[90px] border-gray-300"
              title="Line Spacing"
            >
              <option value="">Spacing</option>
              <option value="1">1.0</option>
              <option value="1.15">1.15</option>
              <option value="1.5">1.5</option>
              <option value="2">2.0</option>
              <option value="2.5">2.5</option>
              <option value="3">3.0</option>
            </select>
              </div>
              </div>

              {/* Text Formatting Group */}
              <div className="inline-flex flex-col border-r border-gray-200 pr-3 mr-3">
                <div className="text-[9px] font-semibold text-gray-500 uppercase mb-1 px-1">Format</div>
                <div className="flex items-center gap-1">
            {/* Text Formatting */}
            <TooltipArrow content="Bold (Ctrl+B)" position="top">
              <button
                type="button"
                onClick={() => execCommand('bold')}
                className={`p-2 md:p-2 touch:p-3 rounded hover:bg-gray-200 active:bg-gray-300 transition-colors min-w-[40px] min-h-[40px] flex items-center justify-center ${activeFormats.bold ? 'bg-brand-blue-100 ring-2 ring-brand-blue-500' : ''}`}
                aria-label="Bold text (Ctrl+B)"
                title="Bold (Ctrl+B)"
              >
                <Bold className={`w-4 h-4 ${activeFormats.bold ? 'text-brand-blue-700' : 'text-gray-700'}`} />
              </button>
            </TooltipArrow>

            <TooltipArrow content="Italic (Ctrl+I)" position="top">
              <button
                type="button"
                onClick={() => execCommand('italic')}
                className={`p-2 rounded hover:bg-gray-200 transition-colors ${activeFormats.italic ? 'bg-brand-blue-100 ring-2 ring-brand-blue-500' : ''}`}
                aria-label="Italic text (Ctrl+I)"
                title="Italic (Ctrl+I)"
              >
                <Italic className={`w-4 h-4 ${activeFormats.italic ? 'text-brand-blue-700' : 'text-gray-700'}`} />
              </button>
            </TooltipArrow>

            <TooltipArrow content="Underline (Ctrl+U)" position="top">
              <button
                type="button"
                onClick={() => execCommand('underline')}
                className={`p-2 rounded hover:bg-gray-200 transition-colors ${activeFormats.underline ? 'bg-brand-blue-100 ring-2 ring-brand-blue-500' : ''}`}
                aria-label="Underline text (Ctrl+U)"
                title="Underline (Ctrl+U)"
              >
                <Underline className={`w-4 h-4 ${activeFormats.underline ? 'text-brand-blue-700' : 'text-gray-700'}`} />
              </button>
            </TooltipArrow>

            <TooltipArrow content="Strikethrough" position="top">
              <button
                type="button"
                onClick={() => execCommand('strikeThrough')}
                className={`p-2 rounded hover:bg-gray-200 transition-colors ${activeFormats.strikeThrough ? 'bg-brand-blue-100 ring-2 ring-brand-blue-500' : ''}`}
                aria-label="Strikethrough text"
                title="Strikethrough"
              >
                <Strikethrough className={`w-4 h-4 ${activeFormats.strikeThrough ? 'text-brand-blue-700' : 'text-gray-700'}`} />
              </button>
            </TooltipArrow>

            <TooltipArrow content="Subscript" position="top">
              <button
                type="button"
                onClick={() => execCommand('subscript')}
                className={`p-2 rounded hover:bg-gray-200 transition-colors ${activeFormats.subscript ? 'bg-brand-blue-100 ring-2 ring-brand-blue-500' : ''}`}
                aria-label="Subscript text"
                title="Subscript"
              >
                <Subscript className={`w-4 h-4 ${activeFormats.subscript ? 'text-brand-blue-700' : 'text-gray-700'}`} />
              </button>
            </TooltipArrow>

            <TooltipArrow content="Superscript" position="top">
              <button
                type="button"
                onClick={() => execCommand('superscript')}
                className={`p-2 rounded hover:bg-gray-200 transition-colors ${activeFormats.superscript ? 'bg-brand-blue-100 ring-2 ring-brand-blue-500' : ''}`}
                aria-label="Superscript text"
                title="Superscript"
              >
                <Superscript className={`w-4 h-4 ${activeFormats.superscript ? 'text-brand-blue-700' : 'text-gray-700'}`} />
              </button>
            </TooltipArrow>

            <div className="w-px h-6 bg-gray-300 mx-1"></div>

            {/* Text Color */}
            <div className="relative">
              <TooltipArrow content="Text Color" position="top">
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (!showColorPicker) saveSelection();
                  }}
                  onClick={() => {
                    setShowColorPicker(!showColorPicker);
                    setShowBgColorPicker(false);
                  }}
                  className={`p-2 rounded hover:bg-gray-200 transition-colors flex flex-col items-center gap-0.5 ${showColorPicker ? 'bg-brand-blue-100 ring-2 ring-brand-blue-500' : ''}`}
                >
                  <Palette className="w-4 h-4 text-gray-700" />
                  <div
                    className="w-4 h-1 rounded-sm"
                    style={{ backgroundColor: activeFormats.foreColor || '#000000' }}
                  />
                </button>
              </TooltipArrow>
            </div>

            {/* Background Color / Highlight */}
            <div className="relative">
              <TooltipArrow content="Highlight Color" position="top">
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (!showBgColorPicker) saveSelection();
                  }}
                  onClick={() => {
                    setShowBgColorPicker(!showBgColorPicker);
                    setShowColorPicker(false);
                  }}
                  className={`p-2 rounded hover:bg-gray-200 transition-colors flex flex-col items-center gap-0.5 ${showBgColorPicker ? 'bg-brand-blue-100 ring-2 ring-brand-blue-500' : ''}`}
                >
                  <Highlighter className="w-4 h-4 text-gray-700" />
                  <div
                    className="w-4 h-1 rounded-sm border border-gray-300"
                    style={{ backgroundColor: activeFormats.hiliteColor || 'transparent' }}
                  />
                </button>
              </TooltipArrow>
            </div>

            <div className="w-px h-6 bg-gray-300 mx-1"></div>

            {/* Text Alignment */}
            <TooltipArrow content="Align Left" position="top">
              <button
                type="button"
                onClick={() => execCommand('justifyLeft')}
                className={`p-2 rounded hover:bg-gray-200 transition-colors ${activeFormats.justifyLeft ? 'bg-brand-blue-100 ring-2 ring-brand-blue-500' : ''}`}
              >
                <AlignLeft className={`w-4 h-4 ${activeFormats.justifyLeft ? 'text-brand-blue-700' : 'text-gray-700'}`} />
              </button>
            </TooltipArrow>

            <TooltipArrow content="Align Center" position="top">
              <button
                type="button"
                onClick={() => execCommand('justifyCenter')}
                className={`p-2 rounded hover:bg-gray-200 transition-colors ${activeFormats.justifyCenter ? 'bg-brand-blue-100 ring-2 ring-brand-blue-500' : ''}`}
              >
                <AlignCenter className={`w-4 h-4 ${activeFormats.justifyCenter ? 'text-brand-blue-700' : 'text-gray-700'}`} />
              </button>
            </TooltipArrow>

            <TooltipArrow content="Align Right" position="top">
              <button
                type="button"
                onClick={() => execCommand('justifyRight')}
                className={`p-2 rounded hover:bg-gray-200 transition-colors ${activeFormats.justifyRight ? 'bg-brand-blue-100 ring-2 ring-brand-blue-500' : ''}`}
              >
                <AlignRight className={`w-4 h-4 ${activeFormats.justifyRight ? 'text-brand-blue-700' : 'text-gray-700'}`} />
              </button>
            </TooltipArrow>

            <TooltipArrow content="Justify" position="top">
              <button
                type="button"
                onClick={() => execCommand('justifyFull')}
                className={`p-2 rounded hover:bg-gray-200 transition-colors ${activeFormats.justifyFull ? 'bg-brand-blue-100 ring-2 ring-brand-blue-500' : ''}`}
              >
                <AlignJustify className={`w-4 h-4 ${activeFormats.justifyFull ? 'text-brand-blue-700' : 'text-gray-700'}`} />
              </button>
            </TooltipArrow>

            <div className="w-px h-6 bg-gray-300 mx-1"></div>

            {/* Lists */}
            <TooltipArrow content="Bullet List" position="top">
              <button
                type="button"
                onClick={() => execCommand('insertUnorderedList')}
                className={`p-2 rounded hover:bg-gray-200 transition-colors ${activeFormats.insertUnorderedList ? 'bg-brand-blue-100 ring-2 ring-brand-blue-500' : ''}`}
              >
                <List className={`w-4 h-4 ${activeFormats.insertUnorderedList ? 'text-brand-blue-700' : 'text-gray-700'}`} />
              </button>
            </TooltipArrow>

            <TooltipArrow content="Numbered List" position="top">
              <button
                type="button"
                onClick={() => execCommand('insertOrderedList')}
                className={`p-2 rounded hover:bg-gray-200 transition-colors ${activeFormats.insertOrderedList ? 'bg-brand-blue-100 ring-2 ring-brand-blue-500' : ''}`}
              >
                <ListOrdered className={`w-4 h-4 ${activeFormats.insertOrderedList ? 'text-brand-blue-700' : 'text-gray-700'}`} />
              </button>
            </TooltipArrow>

            <TooltipArrow content="Decrease Indent" position="top">
              <button
                type="button"
                onClick={() => execCommand('outdent')}
                className="p-2 rounded hover:bg-gray-200 transition-colors"
              >
                <IndentDecrease className="w-4 h-4 text-gray-700" />
              </button>
            </TooltipArrow>

            <TooltipArrow content="Increase Indent" position="top">
              <button
                type="button"
                onClick={() => execCommand('indent')}
                className="p-2 rounded hover:bg-gray-200 transition-colors"
              >
                <IndentIncrease className="w-4 h-4 text-gray-700" />
              </button>
            </TooltipArrow>

            <div className="w-px h-6 bg-gray-300 mx-1"></div>

            {/* Clear Formatting */}
            <TooltipArrow content="Clear Formatting" position="top">
              <button
                type="button"
                onClick={() => execCommand('removeFormat')}
                className="p-2 rounded hover:bg-gray-200 transition-colors"
              >
                <Eraser className="w-4 h-4 text-gray-700" />
              </button>
            </TooltipArrow>

            <div className="w-px h-6 bg-gray-300 mx-1"></div>

            {/* Undo/Redo */}
            <TooltipArrow content="Undo (Ctrl+Z)" position="top">
              <button
                type="button"
                onClick={handleUndo}
                className="p-2 rounded hover:bg-gray-200 transition-colors"
              >
                <Undo className="w-4 h-4 text-gray-700" />
              </button>
            </TooltipArrow>

            <TooltipArrow content="Redo (Ctrl+Y)" position="top">
              <button
                type="button"
                onClick={handleRedo}
                className="p-2 rounded hover:bg-gray-200 transition-colors"
              >
                <Redo className="w-4 h-4 text-gray-700" />
              </button>
            </TooltipArrow>
                </div>
              </div>
            </div>
          )}

          {/* INSERT TAB CONTENT */}
          {!hideInsertTab && activeRibbonTab === 'insert' && (
            <div className="p-3 bg-white" dir="ltr">
              {/* Media & Code Group */}
              <div className="inline-flex flex-col border-r border-gray-200 pr-3 mr-3">
                <div className="text-[9px] font-semibold text-gray-500 uppercase mb-1 px-1">Media</div>
                <div className="flex flex-wrap items-center gap-2">
            {/* Hidden file inputs for uploads */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
            <input
              ref={audioInputRef}
              type="file"
              accept="audio/*"
              onChange={handleAudioUpload}
              className="hidden"
            />
            <input
              ref={videoInputRef}
              type="file"
              accept="video/mp4,.mp4"
              onChange={handleVideoUpload}
              className="hidden"
            />

            {/* Image Dropdown */}
            <div className="relative">
              <button
                type="button"
                data-media-toggle
                onClick={() => {
                  setShowImageDropdown(!showImageDropdown);
                  setShowAudioDropdown(false);
                  setShowVideoDropdown(false);
                  setShowEmojiPicker(false);
                }}
                disabled={uploadingImage}
                className="px-3 py-1.5 rounded bg-blue-500 hover:bg-blue-600 text-white transition-colors flex items-center gap-1 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ImageIcon className="w-4 h-4" />
                {uploadingImage ? 'Uploading...' : 'Image'}
                <ChevronDown className="w-3 h-3 ml-0.5" />
              </button>
              {showImageDropdown && (
                <div className="media-dropdown absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-20 min-w-[160px] py-1">
                  <button
                    type="button"
                    onClick={() => {
                      fileInputRef.current?.click();
                      setShowImageDropdown(false);
                    }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                  >
                    <Upload className="w-4 h-4 text-blue-500" />
                    Upload Image
                  </button>
                  <button
                    type="button"
                    onClick={() => openMediaInsertModal('image-url')}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                  >
                    <Link2 className="w-4 h-4 text-blue-500" />
                    Insert from URL
                  </button>
                </div>
              )}
            </div>

            {/* Audio Dropdown — hidden for scene editing */}
            {!hideAudioInsert && (
            <div className="relative">
              <button
                type="button"
                data-media-toggle
                onClick={() => {
                  setShowAudioDropdown(!showAudioDropdown);
                  setShowImageDropdown(false);
                  setShowVideoDropdown(false);
                  setShowEmojiPicker(false);
                }}
                disabled={uploadingAudio}
                className="px-3 py-1.5 rounded bg-green-500 hover:bg-green-600 text-white transition-colors flex items-center gap-1 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Music className="w-4 h-4" />
                {uploadingAudio ? 'Uploading...' : 'Audio'}
                <ChevronDown className="w-3 h-3 ml-0.5" />
              </button>
              {showAudioDropdown && (
                <div className="media-dropdown absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-20 min-w-[160px] py-1">
                  <button
                    type="button"
                    onClick={() => {
                      audioInputRef.current?.click();
                      setShowAudioDropdown(false);
                    }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                  >
                    <Upload className="w-4 h-4 text-green-500" />
                    Upload Audio
                  </button>
                  <button
                    type="button"
                    onClick={() => openMediaInsertModal('audio-url')}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                  >
                    <Link2 className="w-4 h-4 text-green-500" />
                    Insert from URL
                  </button>
                </div>
              )}
            </div>
            )}

            {/* Video Dropdown — hidden for scene editing */}
            {!hideVideoInsert && (
            <div className="relative">
              <button
                type="button"
                data-media-toggle
                onClick={() => {
                  setShowVideoDropdown(!showVideoDropdown);
                  setShowImageDropdown(false);
                  setShowAudioDropdown(false);
                  setShowEmojiPicker(false);
                }}
                disabled={uploadingVideo}
                className="px-3 py-1.5 rounded bg-purple-500 hover:bg-purple-600 text-white transition-colors flex items-center gap-1 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Video className="w-4 h-4" />
                {uploadingVideo ? 'Uploading...' : 'Video'}
                <ChevronDown className="w-3 h-3 ml-0.5" />
              </button>
              {showVideoDropdown && (
                <div className="media-dropdown absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-20 min-w-[180px] py-1">
                  <button
                    type="button"
                    onClick={() => {
                      videoInputRef.current?.click();
                      setShowVideoDropdown(false);
                    }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                  >
                    <Upload className="w-4 h-4 text-purple-500" />
                    Upload Video
                    <span className="ml-auto text-[10px] text-gray-400">MP4</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => openMediaInsertModal('video-url')}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                  >
                    <Link2 className="w-4 h-4 text-purple-500" />
                    Insert from URL
                  </button>
                  <div className="border-t border-gray-200 my-1"></div>
                  <button
                    type="button"
                    onClick={() => openMediaInsertModal('video-embed')}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                  >
                    <Youtube className="w-4 h-4 text-red-500" />
                    Embed YouTube/Vimeo
                  </button>
                </div>
              )}
            </div>
            )}

            {/* Emoji Picker */}
            <div className="relative">
              <TooltipArrow content="Insert Emoji" position="top">
                <button
                  type="button"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  className="px-3 py-1.5 rounded bg-yellow-500 hover:bg-yellow-600 text-white transition-colors flex items-center gap-1 text-sm font-medium"
                >
                  <Smile className="w-4 h-4" />
                  Emoji
                </button>
              </TooltipArrow>
              {showEmojiPicker && (
                <div
                  className="emoji-picker-dropdown absolute top-full left-0 mt-1 p-3 bg-white border border-gray-300 rounded-lg shadow-lg z-20 w-64"
                  onMouseDown={(e) => e.preventDefault()} // Prevent focus loss from editor
                >
                  <div className="text-xs font-semibold text-gray-600 mb-2">Click an emoji to insert:</div>
                  <div className="grid grid-cols-8 gap-1 max-h-48 overflow-y-auto">
                    {[
                      '😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊',
                      '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘',
                      '😗', '😙', '😚', '😋', '😛', '😝', '😜', '🤪',
                      '🤨', '🧐', '🤓', '😎', '🤩', '🥳', '😏', '😒',
                      '👍', '👎', '👏', '🙌', '👐', '🤝', '🙏', '✨',
                      '🎯', '🎓', '📚', '📝', '✅', '❌', '⚠️', '💡',
                      '🔥', '⭐', '🌟', '✔️', '❤️', '💙', '💚', '💛',
                      '💜', '🧡', '🖤', '🤍', '🤎', '💯', '🔔', '📢',
                    ].map((emoji) => (
                      <button
                        key={emoji}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()} // Prevent focus change
                        onClick={() => insertEmoji(emoji)}
                        className="text-2xl hover:bg-gray-100 rounded p-1 transition-colors"
                        title={emoji}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Table */}
            <TooltipArrow content="Insert Table" position="top">
              <button
                type="button"
                onClick={() => setShowTableModal(true)}
                className="px-3 py-1.5 rounded bg-indigo-500 hover:bg-indigo-600 text-white transition-colors flex items-center gap-1 text-sm font-medium"
              >
                <TableIcon className="w-4 h-4" />
                Table
              </button>
            </TooltipArrow>

            {/* Link */}
            <TooltipArrow content="Insert Hyperlink" position="top">
              <button
                type="button"
                onClick={insertLink}
                className="px-3 py-1.5 rounded bg-blue-500 hover:bg-blue-600 text-white transition-colors flex items-center gap-1 text-sm font-medium"
              >
                <LinkIcon className="w-4 h-4" />
                Link
              </button>
            </TooltipArrow>

            {/* Inline Code */}
            <TooltipArrow content="Inline Code (wrap selected text)" position="top">
              <button
                type="button"
                onClick={insertInlineCode}
                className="px-3 py-1.5 rounded bg-gray-600 hover:bg-gray-700 text-white transition-colors flex items-center gap-1 text-sm font-medium"
              >
                <Code className="w-4 h-4" />
                Inline
              </button>
            </TooltipArrow>

            {/* Code Block */}
            <TooltipArrow content="Code Block (multi-line with language)" position="top">
              <button
                type="button"
                onClick={handleInsertCodeWithLanguage}
                className="px-3 py-1.5 rounded bg-gray-700 hover:bg-gray-800 text-white transition-colors flex items-center gap-1 text-sm font-medium"
                aria-label="Insert code block with language selection"
              >
                <Code className="w-4 h-4" />
                Block
              </button>
            </TooltipArrow>

            {/* Blockquote */}
            <TooltipArrow content="Insert Blockquote" position="top">
              <button
                type="button"
                onClick={insertBlockquote}
                className="px-3 py-1.5 rounded bg-purple-500 hover:bg-purple-600 text-white transition-colors flex items-center gap-1 text-sm font-medium"
              >
                <Quote className="w-4 h-4" />
                Quote
              </button>
            </TooltipArrow>

            {/* Horizontal Rule */}
            <TooltipArrow content="Horizontal Divider" position="top">
              <button
                type="button"
                onClick={insertHorizontalRule}
                className="px-3 py-1.5 rounded bg-gray-500 hover:bg-gray-600 text-white transition-colors flex items-center gap-1 text-sm font-medium"
              >
                <Minus className="w-4 h-4" />
                Divider
              </button>
            </TooltipArrow>

            </div>
          </div>

          {/* Learning Components Section */}
          <div>
            <div className="text-[10px] font-semibold text-gray-500 uppercase mb-2 px-2">Learning Components</div>
            <div className="flex flex-wrap items-center gap-2">

            {/* Insert Components - Opens Config Modal */}
            <TooltipArrow content="Configure & Insert Learning Objectives" position="top">
              <button
                type="button"
                onClick={() => openComponentModal('learning-objectives')}
                className="px-3 py-1.5 rounded bg-blue-500 hover:bg-blue-600 text-white transition-colors flex items-center gap-1 text-sm font-medium"
              >
                <CheckSquare className="w-4 h-4" />
                Objectives
              </button>
            </TooltipArrow>

            {!hideKnowledgeCheck && (
            <TooltipArrow content="Configure & Insert Knowledge Check" position="top">
              <button
                type="button"
                onClick={() => openComponentModal('knowledge-check')}
                className="px-3 py-1.5 rounded bg-green-500 hover:bg-green-600 text-white transition-colors flex items-center gap-1 text-sm font-medium"
              >
                <Target className="w-4 h-4" />
                Check
              </button>
            </TooltipArrow>
            )}

            <TooltipArrow content="Configure & Insert Key Takeaways" position="top">
              <button
                type="button"
                onClick={() => openComponentModal('key-takeaways')}
                className="px-3 py-1.5 rounded bg-cyan-500 hover:bg-cyan-600 text-white transition-colors flex items-center gap-1 text-sm font-medium"
              >
                <Lightbulb className="w-4 h-4" />
                Takeaways
              </button>
            </TooltipArrow>

            <TooltipArrow content="Configure & Insert Example Box" position="top">
              <button
                type="button"
                onClick={() => openComponentModal('example-box')}
                className="px-3 py-1.5 rounded bg-amber-500 hover:bg-amber-600 text-white transition-colors flex items-center gap-1 text-sm font-medium"
              >
                <span className="text-sm">📝</span>
                Example
              </button>
            </TooltipArrow>
            </div>
          </div>

          {/* Callouts Section */}
          <div>
            <div className="text-[10px] font-semibold text-gray-500 uppercase mb-2 px-2">Callouts</div>
            <div className="flex flex-wrap items-center gap-2">

            <TooltipArrow content="Insert Tip Callout" position="top">
              <button
                type="button"
                onClick={() => openComponentModal('callout-tip')}
                className="px-3 py-1.5 rounded bg-emerald-500 hover:bg-emerald-600 text-white transition-colors flex items-center gap-1 text-sm font-medium"
              >
                <span className="text-sm">💡</span>
                Tip
              </button>
            </TooltipArrow>

            <TooltipArrow content="Insert Warning Callout" position="top">
              <button
                type="button"
                onClick={() => openComponentModal('callout-warning')}
                className="px-3 py-1.5 rounded bg-red-500 hover:bg-red-600 text-white transition-colors flex items-center gap-1 text-sm font-medium"
              >
                <span className="text-sm">⚠️</span>
                Warning
              </button>
            </TooltipArrow>

            <TooltipArrow content="Insert Note Callout" position="top">
              <button
                type="button"
                onClick={() => openComponentModal('callout-note')}
                className="px-3 py-1.5 rounded bg-gray-500 hover:bg-gray-600 text-white transition-colors flex items-center gap-1 text-sm font-medium"
              >
                <span className="text-sm">📌</span>
                Note
              </button>
            </TooltipArrow>

            <TooltipArrow content="Insert Info Callout" position="top">
              <button
                type="button"
                onClick={() => openComponentModal('callout-info')}
                className="px-3 py-1.5 rounded bg-blue-400 hover:bg-blue-500 text-white transition-colors flex items-center gap-1 text-sm font-medium"
              >
                <span className="text-sm">ℹ️</span>
                Info
              </button>
            </TooltipArrow>
            </div>
          </div>
            </div>
          )}

          {/* FORMAT TAB CONTENT */}
          {!hideFormatTab && activeRibbonTab === 'format' && (
            <div className="p-3 bg-white">
              {/* Colors Group */}
              <div className="inline-flex flex-col border-r border-gray-200 pr-3 mr-3">
                <div className="text-[9px] font-semibold text-gray-500 uppercase mb-1 px-1">Colors</div>
                <div className="flex items-center gap-1">
                  {/* Text Color */}
                  <div className="relative">
                    <TooltipArrow content="Text Color" position="top">
                      <button
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          if (!showColorPicker) saveSelection();
                        }}
                        onClick={() => {
                          setShowColorPicker(!showColorPicker);
                          setShowBgColorPicker(false);
                        }}
                        className={`p-2 rounded hover:bg-gray-200 transition-colors flex flex-col items-center gap-0.5 ${showColorPicker ? 'bg-brand-blue-100 ring-2 ring-brand-blue-500' : ''}`}
                      >
                        <Palette className="w-4 h-4 text-gray-700" />
                        <div
                          className="w-4 h-1 rounded-sm"
                          style={{ backgroundColor: activeFormats.foreColor || '#000000' }}
                        />
                      </button>
                    </TooltipArrow>
                  </div>

                  {/* Highlight Color */}
                  <div className="relative">
                    <TooltipArrow content="Highlight Color" position="top">
                      <button
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          if (!showBgColorPicker) saveSelection();
                        }}
                        onClick={() => {
                          setShowBgColorPicker(!showBgColorPicker);
                          setShowColorPicker(false);
                        }}
                        className={`p-2 rounded hover:bg-gray-200 transition-colors flex flex-col items-center gap-0.5 ${showBgColorPicker ? 'bg-brand-blue-100 ring-2 ring-brand-blue-500' : ''}`}
                      >
                        <Highlighter className="w-4 h-4 text-gray-700" />
                        <div
                          className="w-4 h-1 rounded-sm border border-gray-300"
                          style={{ backgroundColor: activeFormats.hiliteColor || 'transparent' }}
                        />
                      </button>
                    </TooltipArrow>
                  </div>
                </div>
              </div>

              {/* Advanced Formatting Group */}
              <div className="inline-flex flex-col">
                <div className="text-[9px] font-semibold text-gray-500 uppercase mb-1 px-1">Special Text</div>
                <div className="flex items-center gap-1">
                  <TooltipArrow content="Subscript" position="top">
                    <button type="button" onClick={() => execCommand('subscript')} className={`p-2 rounded hover:bg-gray-200 transition-colors ${activeFormats.subscript ? 'bg-brand-blue-100 ring-2 ring-brand-blue-500' : ''}`} aria-label="Subscript text">
                      <Subscript className={`w-4 h-4 ${activeFormats.subscript ? 'text-brand-blue-700' : 'text-gray-700'}`} />
                    </button>
                  </TooltipArrow>

                  <TooltipArrow content="Superscript" position="top">
                    <button type="button" onClick={() => execCommand('superscript')} className={`p-2 rounded hover:bg-gray-200 transition-colors ${activeFormats.superscript ? 'bg-brand-blue-100 ring-2 ring-brand-blue-500' : ''}`} aria-label="Superscript text">
                      <Superscript className={`w-4 h-4 ${activeFormats.superscript ? 'text-brand-blue-700' : 'text-gray-700'}`} />
                    </button>
                  </TooltipArrow>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Color Picker Dropdowns - Render outside tabs so they work in all tabs */}
      {showColorPicker && (
        <div
          className="color-picker-dropdown absolute top-12 left-4 p-3 bg-white border border-gray-200 rounded-lg shadow-xl z-[100] min-w-[240px]"
          onMouseDown={(e) => e.preventDefault()}
        >
          <div className="text-xs font-bold text-gray-700 mb-3 flex items-center gap-2">
            <Palette className="w-4 h-4" />
            Text Color
          </div>
          <div className="space-y-3">
            {/* Standard Colors */}
            <div>
              <div className="text-[10px] font-semibold text-gray-500 uppercase mb-2">Standard</div>
              <div className="grid grid-cols-5 gap-2">
                {[
                  { color: '#000000', name: 'Black' },
                  { color: '#374151', name: 'Gray' },
                  { color: '#6b7280', name: 'Slate' },
                  { color: '#9ca3af', name: 'Light Gray' },
                  { color: '#ffffff', name: 'White' },
                ].map(({ color, name }) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => {
                      execCommand('foreColor', color);
                      setShowColorPicker(false);
                    }}
                    className="group relative w-10 h-10 md:w-10 md:h-10 touch:w-12 touch:h-12 rounded-lg border-2 border-gray-300 hover:border-brand-blue-500 active:scale-95 hover:scale-110 transition-all shadow-sm hover:shadow-md"
                    style={{ backgroundColor: color }}
                    title={name}
                    aria-label={`Select ${name} color`}
                  >
                    {color === '#ffffff' && <div className="absolute inset-0 border border-gray-200 rounded-lg"></div>}
                  </button>
                ))}
              </div>
            </div>

            {/* Theme Colors */}
            <div>
              <div className="text-[10px] font-semibold text-gray-500 uppercase mb-2">Theme Colors</div>
              <div className="grid grid-cols-5 gap-2">
                {[
                  { color: '#3b82f6', name: 'Blue' },
                  { color: '#10b981', name: 'Green' },
                  { color: '#f59e0b', name: 'Orange' },
                  { color: '#ef4444', name: 'Red' },
                  { color: '#8b5cf6', name: 'Purple' },
                  { color: '#ec4899', name: 'Pink' },
                  { color: '#06b6d4', name: 'Cyan' },
                  { color: '#84cc16', name: 'Lime' },
                  { color: '#f97316', name: 'Amber' },
                  { color: '#6366f1', name: 'Indigo' },
                ].map(({ color, name }) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => {
                      execCommand('foreColor', color);
                      setShowColorPicker(false);
                    }}
                    className="w-10 h-10 md:w-10 md:h-10 touch:w-12 touch:h-12 rounded-lg border-2 border-gray-300 hover:border-brand-blue-500 active:scale-95 hover:scale-110 transition-all shadow-sm hover:shadow-md"
                    style={{ backgroundColor: color }}
                    title={name}
                    aria-label={`Select ${name} color`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {showBgColorPicker && (
        <div
          className="color-picker-dropdown absolute top-12 left-20 p-3 bg-white border border-gray-200 rounded-lg shadow-xl z-[100] min-w-[240px]"
          onMouseDown={(e) => e.preventDefault()}
        >
          <div className="text-xs font-bold text-gray-700 mb-3 flex items-center gap-2">
            <Highlighter className="w-4 h-4" />
            Highlight Color
          </div>
          <div className="space-y-3">
            {/* No Highlight Option */}
            <div>
              <div className="text-[10px] font-semibold text-gray-500 uppercase mb-2">Remove</div>
              <button
                type="button"
                onClick={() => {
                  execCommand('hiliteColor', 'transparent');
                  setShowBgColorPicker(false);
                }}
                className="w-full px-3 py-2 rounded-lg border-2 border-gray-300 hover:border-red-500 transition-all text-xs font-semibold text-gray-700 hover:bg-red-50"
              >
                ✕ No Highlight
              </button>
            </div>

            {/* Highlight Colors */}
            <div>
              <div className="text-[10px] font-semibold text-gray-500 uppercase mb-2">Highlights</div>
              <div className="grid grid-cols-5 gap-2">
                {[
                  { color: '#fef3c7', name: 'Yellow' },
                  { color: '#fecaca', name: 'Red' },
                  { color: '#bbf7d0', name: 'Green' },
                  { color: '#bfdbfe', name: 'Blue' },
                  { color: '#e9d5ff', name: 'Purple' },
                  { color: '#fbcfe8', name: 'Pink' },
                  { color: '#fed7aa', name: 'Orange' },
                  { color: '#a7f3d0', name: 'Emerald' },
                  { color: '#ddd6fe', name: 'Violet' },
                  { color: '#d1d5db', name: 'Gray' },
                ].map(({ color, name }) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => {
                      execCommand('hiliteColor', color);
                      setShowBgColorPicker(false);
                    }}
                    className="w-10 h-10 md:w-10 md:h-10 touch:w-12 touch:h-12 rounded-lg border-2 border-gray-300 hover:border-brand-blue-500 active:scale-95 hover:scale-110 transition-all shadow-sm hover:shadow-md"
                    style={{ backgroundColor: color }}
                    title={name}
                    aria-label={`Highlight with ${name} color`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contenteditable Editor */}
      <div className="relative visual-editor-container">
        <div
          ref={editorRef}
          contentEditable={!readOnly}
          onInput={handleInput}
          onClick={handleEditorClick}
          onDoubleClick={handleEditorDoubleClick}
          onContextMenu={handleEditorContextMenu}
          onBlur={() => { saveSelection(); flushPendingContent(); }}
          className="editor-content-preview isolate min-h-[400px] p-6 focus:outline-none overflow-auto max-h-[600px]"
          dir={isRTL ? 'rtl' : 'ltr'}
          style={{
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif",
            lineHeight: 1.6,
          }}
          suppressContentEditableWarning
        />

        {/* Table Context Menu */}
        {showTableContextMenu && (
          <div
            className="fixed bg-white border-2 border-gray-300 rounded-lg shadow-2xl py-2 z-50 min-w-[200px]"
            style={{ left: `${tableContextMenuPosition.x}px`, top: `${tableContextMenuPosition.y}px` }}
          >
            <div className="px-3 py-2 text-xs font-bold text-gray-500 uppercase border-b border-gray-200">
              Table Actions
            </div>

            {/* Row Actions */}
            <div className="py-1">
              <button
                type="button"
                onClick={() => addTableRow('above')}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-brand-blue-50 hover:text-brand-blue-700 transition-colors flex items-center gap-2"
              >
                <span>↑</span> Insert Row Above
              </button>
              <button
                type="button"
                onClick={() => addTableRow('below')}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-brand-blue-50 hover:text-brand-blue-700 transition-colors flex items-center gap-2"
              >
                <span>↓</span> Insert Row Below
              </button>
              <button
                type="button"
                onClick={deleteTableRow}
                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
              >
                <span>✕</span> Delete Row
              </button>
            </div>

            <div className="border-t border-gray-200"></div>

            {/* Column Actions */}
            <div className="py-1">
              <button
                type="button"
                onClick={() => addTableColumn('left')}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-brand-blue-50 hover:text-brand-blue-700 transition-colors flex items-center gap-2"
              >
                <span>←</span> Insert Column Left
              </button>
              <button
                type="button"
                onClick={() => addTableColumn('right')}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-brand-blue-50 hover:text-brand-blue-700 transition-colors flex items-center gap-2"
              >
                <span>→</span> Insert Column Right
              </button>
              <button
                type="button"
                onClick={deleteTableColumn}
                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
              >
                <span>✕</span> Delete Column
              </button>
            </div>

            <div className="border-t border-gray-200"></div>

            {/* Table Actions */}
            <div className="py-1">
              <button
                type="button"
                onClick={deleteTable}
                className="w-full px-4 py-2 text-left text-sm text-red-700 font-semibold hover:bg-red-50 transition-colors flex items-center gap-2"
              >
                <span>🗑️</span> Delete Entire Table
              </button>
            </div>
          </div>
        )}
      </div>


      {/* Component Configuration Modal */}
      <ComponentConfigModal
        isOpen={componentModalOpen}
        type={componentModalType}
        onClose={() => {
          setComponentModalOpen(false);
          setEditingComponentElement(null);
        }}
        onInsert={handleComponentInsert}
        editingElement={editingComponentElement}
      />

      {/* Media Insert Modal (Image/Audio/Video URL + YouTube/Vimeo Embed) */}
      <MediaInsertModal
        isOpen={mediaInsertModalOpen}
        type={mediaInsertType}
        onClose={() => setMediaInsertModalOpen(false)}
        onInsert={handleMediaInsert}
      />

      {/* Table Insert Modal */}
      {showTableModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-96">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Insert Table</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Number of Rows</label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  defaultValue="3"
                  id="table-rows"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Number of Columns</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  defaultValue="3"
                  id="table-cols"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue-500 focus:outline-none"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={() => setShowTableModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  const rows = parseInt((document.getElementById('table-rows') as HTMLInputElement).value) || 3;
                  const cols = parseInt((document.getElementById('table-cols') as HTMLInputElement).value) || 3;
                  insertTable(rows, cols);
                }}
                className="flex-1 px-4 py-2 bg-brand-blue-600 text-white rounded-lg hover:bg-brand-blue-700 transition-colors font-semibold"
              >
                Insert Table
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Link Insert Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-96">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Insert Hyperlink</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Display Text</label>
                <input
                  type="text"
                  value={linkText}
                  onChange={(e) => setLinkText(e.target.value)}
                  placeholder="Link text (optional)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">URL</label>
                <input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue-500 focus:outline-none"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={() => {
                  setShowLinkModal(false);
                  setLinkUrl('');
                  setLinkText('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmInsertLink}
                className="flex-1 px-4 py-2 bg-brand-blue-600 text-white rounded-lg hover:bg-brand-blue-700 transition-colors font-semibold"
              >
                Insert Link
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Find & Replace Modal */}
      {showFindReplace && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">🔍 Find & Replace</h3>
              <button
                type="button"
                onClick={() => setShowFindReplace(false)}
                className="p-1 hover:bg-gray-100 rounded"
                aria-label="Close find and replace"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Find Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Find</label>
                <input
                  type="text"
                  value={findText}
                  onChange={(e) => setFindText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleFind()}
                  placeholder="Text to find..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue-500 focus:outline-none"
                  autoFocus
                />
              </div>

              {/* Replace Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Replace with</label>
                <input
                  type="text"
                  value={replaceText}
                  onChange={(e) => setReplaceText(e.target.value)}
                  placeholder="Replacement text..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue-500 focus:outline-none"
                />
              </div>

              {/* Options */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="case-sensitive"
                  checked={caseSensitive}
                  onChange={(e) => setCaseSensitive(e.target.checked)}
                  className="w-4 h-4 text-brand-blue-600 border-gray-300 rounded focus:ring-brand-blue-500"
                />
                <label htmlFor="case-sensitive" className="text-sm text-gray-700">
                  Case sensitive
                </label>
              </div>

              {/* Match Counter */}
              {findMatches > 0 && (
                <div className="text-xs text-gray-600 bg-blue-50 px-3 py-2 rounded-lg">
                  Found {findMatches} match{findMatches > 1 ? 'es' : ''}
                </div>
              )}

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={handleFind}
                  disabled={!findText}
                  className="px-4 py-2 bg-brand-blue-600 text-white rounded-lg hover:bg-brand-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-sm"
                >
                  Find
                </button>
                <button
                  type="button"
                  onClick={handleReplace}
                  disabled={!findText || !replaceText}
                  className="px-4 py-2 bg-brand-green-600 text-white rounded-lg hover:bg-brand-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-sm"
                >
                  Replace
                </button>
              </div>
              <button
                type="button"
                onClick={handleReplaceAll}
                disabled={!findText || !replaceText}
                className="w-full px-4 py-2 bg-brand-purple-600 text-white rounded-lg hover:bg-brand-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-sm"
              >
                Replace All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Code Language Selector Modal */}
      {showCodeLanguageModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Select Code Language</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {CODE_LANGUAGES.map((lang) => (
                <button
                  key={lang.value}
                  type="button"
                  onClick={() => {
                    insertCodeBlock(lang.value);
                    setShowCodeLanguageModal(false);
                  }}
                  className="flex flex-col items-center gap-2 p-4 border-2 border-gray-200 rounded-xl hover:border-brand-blue-500 hover:bg-brand-blue-50 transition-all group"
                >
                  <span className="text-3xl">{lang.emoji}</span>
                  <span className="text-sm font-semibold text-gray-700 group-hover:text-brand-blue-700">{lang.label}</span>
                </button>
              ))}
            </div>
            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={() => setShowCodeLanguageModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Keyboard Shortcuts Help Modal */}
      {showKeyboardShortcuts && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" dir="ltr" style={{ direction: 'ltr', textAlign: 'left' }}>
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md max-h-[80vh] overflow-y-auto custom-scrollbar" style={{ color: '#111827' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold" style={{ color: '#111827' }}>Keyboard Shortcuts</h3>
              <button
                type="button"
                onClick={() => setShowKeyboardShortcuts(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-4 h-4" style={{ color: '#6b7280' }} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Editing */}
              <div>
                <h4 className="text-sm font-semibold mb-2" style={{ color: '#374151' }}>Editing</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span style={{ color: '#4b5563' }}>Save</span>
                    <kbd className="px-2 py-0.5 rounded text-xs" style={{ backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', color: '#374151' }}>Ctrl+S</kbd>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: '#4b5563' }}>Bold</span>
                    <kbd className="px-2 py-0.5 rounded text-xs" style={{ backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', color: '#374151' }}>Ctrl+B</kbd>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: '#4b5563' }}>Italic</span>
                    <kbd className="px-2 py-0.5 rounded text-xs" style={{ backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', color: '#374151' }}>Ctrl+I</kbd>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: '#4b5563' }}>Underline</span>
                    <kbd className="px-2 py-0.5 rounded text-xs" style={{ backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', color: '#374151' }}>Ctrl+U</kbd>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: '#4b5563' }}>Undo</span>
                    <kbd className="px-2 py-0.5 rounded text-xs" style={{ backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', color: '#374151' }}>Ctrl+Z</kbd>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: '#4b5563' }}>Redo</span>
                    <kbd className="px-2 py-0.5 rounded text-xs" style={{ backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', color: '#374151' }}>Ctrl+Y</kbd>
                  </div>
                </div>
              </div>

              {/* Search */}
              <div>
                <h4 className="text-sm font-semibold mb-2" style={{ color: '#374151' }}>Search</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span style={{ color: '#4b5563' }}>Find</span>
                    <kbd className="px-2 py-0.5 rounded text-xs" style={{ backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', color: '#374151' }}>Ctrl+F</kbd>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: '#4b5563' }}>Replace</span>
                    <kbd className="px-2 py-0.5 rounded text-xs" style={{ backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', color: '#374151' }}>Ctrl+H</kbd>
                  </div>
                </div>
              </div>

              {/* Navigation */}
              <div>
                <h4 className="text-sm font-semibold mb-2" style={{ color: '#374151' }}>Navigation</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span style={{ color: '#4b5563' }}>Close pickers/modals</span>
                    <kbd className="px-2 py-0.5 rounded text-xs" style={{ backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', color: '#374151' }}>Esc</kbd>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: '#4b5563' }}>Show shortcuts</span>
                    <kbd className="px-2 py-0.5 rounded text-xs" style={{ backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', color: '#374151' }}>Ctrl+/</kbd>
                  </div>
                </div>
              </div>

              {/* Tips */}
              <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe' }}>
                <p className="text-xs" style={{ color: '#1e3a5f' }}>
                  <strong>Tip:</strong> Click anywhere in the editor to start typing. Use toolbar buttons or keyboard shortcuts for formatting.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShowKeyboardShortcuts(false)}
              className="w-full mt-4 px-4 py-2 rounded-lg transition-colors font-semibold"
              style={{ backgroundColor: '#1f2937', color: '#ffffff' }}
            >
              Got it!
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default forwardRef<VisualContentEditorHandle, VisualContentEditorProps>(VisualContentEditor);